// NetworkGraph renders the interactive D3 canvas for survey nominations.
// It combines Firestore data, dimension toggles, a draggable legend, and the HUD panel.

import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import * as d3 from "d3";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Slider,
  Select,
  MenuItem,
  Typography,
  Divider,
  Tooltip,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  TextField,
  IconButton,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  VisibilityOff,
  Visibility,
  HelpOutline,
  InfoOutlined as InfoOutlinedIcon,
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as CenterFocusStrongIcon,
  Download as DownloadIcon,
  CompareArrows as CompareArrowsIcon,
  RestartAlt as RestartAltIcon,
} from "@mui/icons-material";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  useNetworkData,
  NodeDatum,
  LinkDatum,
  RawNomEdge,
  Dimension,
  DIM_COMBINED,
  HOW_UNLABELED,
} from "../hooks/useNetworkData";
import { buildEdgeColorScale } from "../utils/edgeColors";
import * as cola from "webcola";
import { auth, db } from "../firebase";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";

// Mark links that have an opposite-direction counterpart (used for curve offsets)
function markReverseEdges(list: LinkDatum[]) {
  const seen = new Set<string>();
  const reverseKeys = new Set<string>();
  list.forEach((link) => {
    const key = `${link.sid}→${link.tid}`;
    const reverseKey = `${link.tid}→${link.sid}`;
    if (link.sid !== link.tid && seen.has(reverseKey)) {
      reverseKeys.add(key);
      reverseKeys.add(reverseKey);
    }
    seen.add(key);
  });
  list.forEach((link) => {
    const key = `${link.sid}→${link.tid}`;
    link._hasReverse = reverseKeys.has(key);
  });
}

const CONTROL_LABEL_SX: SxProps<Theme> = { fontSize: "0.72rem" };
const COLA_DEBUG = true;
const SECTION_TITLE_SX: SxProps<Theme> = {
  color: "text.secondary",
  letterSpacing: "0.08em",
  display: "block",
  mb: 0.1,
  fontSize: "0.65rem",
};
const CONTROL_BUTTON_SX: SxProps<Theme> = { fontSize: "0.75rem", py: 0.15, px: 0.9, minHeight: 30 };
const TOOLTIP_ENTER_DELAY = 250;
const LABEL_FONT_FAMILY = "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial";
const LABEL_BASE_GAP = 6;

type ControlCardProps = {
  children: ReactNode;
  flex?: string | number;
  minWidth?: number;
  sx?: SxProps<Theme>;
};

function ControlCard({ children, flex = "1 1 150px", minWidth = 130, sx }: ControlCardProps) {
  return (
    <Box
      sx={{
        flex,
        minWidth,
        display: "flex",
        flexDirection: "column",
        gap: 0.2,
        py: 0.1,
        "& .MuiSlider-root": { mt: -0.75 },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

type ControlGroupProps = {
  title: string;
  children: ReactNode;
  sx?: SxProps<Theme>;
};

type LabelBox = {
  rectHeight: number;
  rectWidth: number;
  offset: number;
  width: number;
  height: number;
};

type NetworkUiSettings = {
  layoutEngine: "d3" | "cola";
  mode: "perHow" | "aggregate" | "rawNomination";
  selectedDim: string;
  selectedRespondent: number | "ALL";
  sizeMode: "fixed" | "degree";
  colorScheme: "blue" | "category10" | "turbo";
  edgeWidthMode: "fixed" | "weight";
  showArrows: boolean;
  hoverHighlightEnabled: boolean;
  nodeHoverTooltipEnabled: boolean;
  hideIsolates: boolean;
  showSelfLoops: boolean;
  highlightBidirectional: boolean;
  dupOffset: number;
  charge: number;
  chargeMaxDist: number;
  linkDistance: number;
  linkStrength: number;
  collide: boolean;
  collideRadius: number;
  alphaDecay: number;
  gravity: number;
  labelSize: number;
  labelSpacingScale: number;
  labelPadding: number;
  labelWidthCap: number;
  labelShowBackground: boolean;
  labelBackgroundPadding: number;
  labelBackgroundOpacity: number;
  labelBackgroundColor: string;
  labelBackgroundRadius: number;
  colaAvoidOverlaps: boolean;
  colaIterations: number;
  colaOverlapPadding: number;
  colaLinkLengthStrategy: "fixed" | "jaccard";
  colaLinkLengthWeight: number;
  colaFlowAxis: "none" | "x" | "y" | "x-stagger" | "y-stagger" | "radial";
  colaFlowSeparation: number;
  colaHandleDisconnected: boolean;
  colaPinAfterDrag: boolean;
  colaAutoStopMs: number;
  colaConvergenceThreshold: number;
  colaDragAlpha: number;
  colaSettleAlpha: number;
};

const DEFAULT_NETWORK_UI_SETTINGS: NetworkUiSettings = {
  layoutEngine: "cola",
  mode: "perHow",
  selectedDim: DIM_COMBINED,
  selectedRespondent: "ALL",
  sizeMode: "degree",
  colorScheme: "category10",
  edgeWidthMode: "weight",
  showArrows: true,
  hoverHighlightEnabled: true,
  nodeHoverTooltipEnabled: false,
  hideIsolates: false,
  showSelfLoops: true,
  highlightBidirectional: false,
  dupOffset: 16,
  charge: -350,
  chargeMaxDist: 280,
  linkDistance: 120,
  linkStrength: 0.6,
  collide: true,
  collideRadius: 16,
  alphaDecay: 0.1,
  gravity: 0.03,
  labelSize: 12,
  labelSpacingScale: 1,
  labelPadding: 0,
  labelWidthCap: 180,
  labelShowBackground: true,
  labelBackgroundPadding: 4,
  labelBackgroundOpacity: 0.85,
  labelBackgroundColor: "#ffffff",
  labelBackgroundRadius: 4,
  colaAvoidOverlaps: true,
  colaIterations: 60,
  colaOverlapPadding: 0,
  colaLinkLengthStrategy: "fixed",
  colaLinkLengthWeight: 1,
  colaFlowAxis: "none",
  colaFlowSeparation: 80,
  colaHandleDisconnected: true,
  colaPinAfterDrag: false,
  colaAutoStopMs: 600,
  colaConvergenceThreshold: 0.01,
  colaDragAlpha: 0.18,
  colaSettleAlpha: 0.1,
};

type NodeMetricRow = {
  id: string;
  inDegree: number;
  outDegree: number;
  degree: number;
  betweenness: number;
  closeness: number;
};

function computeNodeMetrics(nodeIds: string[], graphLinks: LinkDatum[]): Map<string, NodeMetricRow> {
  const metrics = new Map<string, NodeMetricRow>();
  nodeIds.forEach((id) =>
    metrics.set(id, {
      id,
      inDegree: 0,
      outDegree: 0,
      degree: 0,
      betweenness: 0,
      closeness: 0,
    })
  );

  graphLinks.forEach((e) => {
    const sid = String(e.sid ?? "");
    const tid = String(e.tid ?? "");
    const w = Number(e.w ?? 1);
    const out = metrics.get(sid);
    const inn = metrics.get(tid);
    if (out) {
      out.outDegree += w;
      out.degree += w;
    }
    if (inn) {
      inn.inDegree += w;
      inn.degree += w;
    }
  });

  const adj = new Map<string, string[]>();
  nodeIds.forEach((n) => adj.set(n, []));
  graphLinks.forEach((e) => {
    const sid = String(e.sid ?? "");
    const tid = String(e.tid ?? "");
    if (!adj.has(sid) || !adj.has(tid)) return;
    adj.get(sid)!.push(tid);
  });

  // Directed, unweighted Brandes (same approach as Network Details view)
  nodeIds.forEach((s) => {
    const stack: string[] = [];
    const preds = new Map<string, string[]>();
    const sigma = new Map<string, number>();
    const dist = new Map<string, number>();

    nodeIds.forEach((v) => {
      preds.set(v, []);
      sigma.set(v, 0);
      dist.set(v, -1);
    });
    sigma.set(s, 1);
    dist.set(s, 0);

    const queue: string[] = [s];
    while (queue.length) {
      const v = queue.shift()!;
      stack.push(v);
      (adj.get(v) || []).forEach((w) => {
        if (dist.get(w)! < 0) {
          dist.set(w, dist.get(v)! + 1);
          queue.push(w);
        }
        if (dist.get(w) === dist.get(v)! + 1) {
          sigma.set(w, sigma.get(w)! + sigma.get(v)!);
          preds.get(w)!.push(v);
        }
      });
    }

    const delta = new Map<string, number>();
    nodeIds.forEach((v) => delta.set(v, 0));
    while (stack.length) {
      const w = stack.pop()!;
      preds.get(w)!.forEach((v) => {
        const c = (sigma.get(v)! / sigma.get(w)!) * (1 + delta.get(w)!);
        delta.set(v, delta.get(v)! + c);
      });
      if (w !== s) {
        const m = metrics.get(w);
        if (m) m.betweenness += delta.get(w)!;
      }
    }
  });

  nodeIds.forEach((s) => {
    const dist = new Map<string, number>();
    nodeIds.forEach((n) => dist.set(n, -1));
    dist.set(s, 0);
    const queue: string[] = [s];
    while (queue.length) {
      const v = queue.shift()!;
      const dv = dist.get(v)!;
      (adj.get(v) || []).forEach((w) => {
        if (dist.get(w)! < 0) {
          dist.set(w, dv + 1);
          queue.push(w);
        }
      });
    }
    let reach = 0;
    let sum = 0;
    dist.forEach((d) => {
      if (d > 0) {
        reach += 1;
        sum += d;
      }
    });
    const m = metrics.get(s);
    if (m && sum > 0) m.closeness = reach / sum;
  });

  return metrics;
}

function ControlGroup({ title, children, sx }: ControlGroupProps) {
  return (
    <Box sx={{ width: "auto", ...sx }}>
      <Typography variant="overline" sx={SECTION_TITLE_SX}>
        {title}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          columnGap: 0.6,
          rowGap: 0.4,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export default function NetworkGraph() {
  const { id: surveyId, source, target } = useParams<{ id: string; source: string; target: string }>();
  const svgRef = useRef<SVGSVGElement>(null);
  const zoomBehavior = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const simRef = useRef<d3.Simulation<NodeDatum, LinkDatum> | null>(null);
  const colaRef = useRef<any>(null);
  const controlsRef = useRef<HTMLDivElement | null>(null);
  const colaStopTimerRef = useRef<number | null>(null);
  const settingsLoadedRef = useRef(false);
  const settingsLoadingRef = useRef(false);
  const settingsSaveTimerRef = useRef<number | null>(null);
  const [settingsUid, setSettingsUid] = useState<string | null>(null);
  const d3SelectionsRef = useRef<{
    linkSel: d3.Selection<SVGPathElement, LinkDatum, SVGGElement, unknown>;
    nodeSel: d3.Selection<SVGCircleElement, NodeDatum, SVGGElement, unknown>;
    labelSel: d3.Selection<SVGGElement, NodeDatum, SVGGElement, unknown>;
    updateNow: () => void;
  } | null>(null);
  const colaSelectionsRef = useRef<{
    linkSel: d3.Selection<SVGPathElement, LinkDatum, SVGGElement, unknown>;
    nodeSel: d3.Selection<SVGCircleElement, any, SVGGElement, unknown>;
    labelSel: d3.Selection<SVGGElement, any, SVGGElement, unknown>;
    colaNodes: any[];
    colaLinks: any[];
    updateCola: () => void;
  } | null>(null);
  const d3GeomRef = useRef<{
    rScale: (d: NodeDatum) => number;
    dupOffset: number;
    getLabelBox: (d: NodeDatum) => LabelBox;
  }>({
    rScale: (_: NodeDatum) => 0,
    dupOffset: 0,
    getLabelBox: (_: NodeDatum) => ({ rectHeight: 0, rectWidth: 0, offset: 0, width: 0, height: 0 }),
  });
  const colaGeomRef = useRef<{
    rScale: (d: NodeDatum) => number;
    dupOffset: number;
    getLabelBox: (d: NodeDatum) => LabelBox;
  }>({
    rScale: (_: NodeDatum) => 0,
    dupOffset: 0,
    getLabelBox: (_: NodeDatum) => ({ rectHeight: 0, rectWidth: 0, offset: 0, width: 0, height: 0 }),
  });

  // Viewport sizing
  const [wh, setWh] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setWh({ w: window.innerWidth, h: window.innerHeight }));
    };
    window.addEventListener("resize", onResize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); };
  }, []);

  // Raw response-derived data
  const { rawNomEdges, nodes, dimensions, howChoiceOrder } = useNetworkData({
    surveyId,
    source,
    target,
  });

  const exportGraphImage = async () => {
    if (!svgRef.current) return;
    try {
      setExportingImage(true);
      const svgEl = svgRef.current;
      const serializer = new XMLSerializer();
      const source = serializer.serializeToString(svgEl);
      const svgBlob = new Blob([`<?xml version="1.0" standalone="no"?>\n${source}`], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(svgBlob);

      const img = new Image();
      const viewBox = svgEl.viewBox.baseVal;
      const bbox = svgEl.getBoundingClientRect();
      const width = viewBox?.width || bbox.width || 1200;
      const height = viewBox?.height || bbox.height || 800;
      const scale = window.devicePixelRatio || 2;

      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = (e) => reject(e);
        img.src = url;
      });

      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(width * scale));
      canvas.height = Math.max(1, Math.round(height * scale));
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Could not get canvas context");
      ctx.scale(scale, scale);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      const pngUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = pngUrl;
      a.download = `network-${surveyId || "graph"}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to export graph image", err);
    } finally {
      setExportingImage(false);
    }
  };

  // Respondent filter state
  const [selectedRespondent, setSelectedRespondent] = useState<number | "ALL">("ALL");

  // Force + visual controls
  const [charge, setCharge] = useState(-350);
  const [linkDistance, setLinkDistance] = useState(120);
  const [linkStrength, setLinkStrength] = useState(0.6);
  const [collide, setCollide] = useState(true);
  const [collideRadius, setCollideRadius] = useState(16);
  const [alphaDecay, setAlphaDecay] = useState(0.1);
const [sizeMode, setSizeMode] = useState<"fixed" | "degree">("degree");
const [colorScheme, setColorScheme] = useState<"blue" | "category10" | "turbo">("category10");
const [labelSize, setLabelSize] = useState(12);
const [labelSpacingScale, setLabelSpacingScale] = useState(1);
const [labelPadding, setLabelPadding] = useState(0);
const [labelWidthCap, setLabelWidthCap] = useState(180);
const [labelShowBackground, setLabelShowBackground] = useState(true);
const [labelBackgroundPadding, setLabelBackgroundPadding] = useState(4);
const [labelBackgroundOpacity, setLabelBackgroundOpacity] = useState(0.85);
const [labelBackgroundColor, setLabelBackgroundColor] = useState("#ffffff");
const [labelBackgroundRadius, setLabelBackgroundRadius] = useState(4);
const [edgeWidthMode, setEdgeWidthMode] = useState<"fixed" | "weight">("weight");
  const [showArrows, setShowArrows] = useState(true);
  const [dupOffset, setDupOffset] = useState(16);
const [hideIsolates, setHideIsolates] = useState(false);
const [isDrawerOpen, setIsDrawerOpen] = useState(false);
const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
const [hoverHighlightEnabled, setHoverHighlightEnabled] = useState(true);
const [nodeHoverTooltipEnabled, setNodeHoverTooltipEnabled] = useState(false);
const [nodeHoverTooltip, setNodeHoverTooltip] = useState<{ id: string; x: number; y: number } | null>(null);
const [isFrozen, setIsFrozen] = useState(false);
const [gravity, setGravity] = useState(0.03);
const [chargeMaxDist, setChargeMaxDist] = useState<number>(280);
const [showSelfLoops, setShowSelfLoops] = useState(true);
const [controlsHeight, setControlsHeight] = useState(0);
const [layoutEngine, setLayoutEngine] = useState<"d3" | "cola">("cola");
const isCola = layoutEngine === "cola";
const [ribbonTab, setRibbonTab] = useState<"view" | "data" | "labels" | "layout" | "dynamics" | "advanced">("view");
const [colaAvoidOverlaps, setColaAvoidOverlaps] = useState(true);
const [colaIterations, setColaIterations] = useState(60);
const [colaOverlapPadding, setColaOverlapPadding] = useState(0);
const [exportingImage, setExportingImage] = useState(false);
const [colaPinned, setColaPinned] = useState<Record<string, { x: number; y: number }>>({});
const [colaLinkLengthStrategy, setColaLinkLengthStrategy] = useState<"fixed" | "jaccard">("fixed");
const [colaLinkLengthWeight, setColaLinkLengthWeight] = useState(1);
  type ColaFlowMode = "none" | "x" | "y" | "x-stagger" | "y-stagger" | "radial";
  const [colaFlowAxis, setColaFlowAxis] = useState<ColaFlowMode>("none");
const [colaFlowSeparation, setColaFlowSeparation] = useState(80);
const [colaHandleDisconnected, setColaHandleDisconnected] = useState(true);
const [colaPinAfterDrag, setColaPinAfterDrag] = useState(false);
const [colaAutoStopMs, setColaAutoStopMs] = useState(600);
const [colaConvergenceThreshold, setColaConvergenceThreshold] = useState(0.01);
const [colaDragAlpha, setColaDragAlpha] = useState(0.18);
const [colaSettleAlpha, setColaSettleAlpha] = useState(0.1);
const [highlightBidirectional, setHighlightBidirectional] = useState(false);

  // Mode & legend selections
  type Mode = "perHow" | "aggregate" | "rawNomination";
  const [mode, setMode] = useState<Mode>("perHow");
  const [activeHow, setActiveHow] = useState<Set<string>>(new Set());

  // Dimension selector state
  const [selectedDim, setSelectedDim] = useState<string>(DIM_COMBINED);

  useEffect(() => {
    if (!dimensions.length) return;
    if (!dimensions.find((d) => d.name === selectedDim)) {
      setSelectedDim(DIM_COMBINED);
    }
  }, [dimensions, selectedDim]);

  useEffect(() => {
    if (mode === "perHow" && highlightBidirectional) {
      setHighlightBidirectional(false);
    }
  }, [mode, highlightBidirectional]);

  const networkSettingsKey = useMemo(() => {
    if (!surveyId || !source || !target) return null;
    return `${surveyId}::${source}::${target}`;
  }, [surveyId, source, target]);

  const applySettings = (settings?: Partial<NetworkUiSettings>) => {
    const merged = { ...DEFAULT_NETWORK_UI_SETTINGS, ...(settings ?? {}) };
    setLayoutEngine(merged.layoutEngine);
    setMode(merged.mode);
    setSelectedDim(merged.selectedDim);
    setSelectedRespondent(merged.selectedRespondent);
    setSizeMode(merged.sizeMode);
    setColorScheme(merged.colorScheme);
    setEdgeWidthMode(merged.edgeWidthMode);
    setShowArrows(merged.showArrows);
    setHoverHighlightEnabled(merged.hoverHighlightEnabled);
    setNodeHoverTooltipEnabled(merged.nodeHoverTooltipEnabled);
    setHideIsolates(merged.hideIsolates);
    setShowSelfLoops(merged.showSelfLoops);
    setHighlightBidirectional(merged.highlightBidirectional);
    setDupOffset(merged.dupOffset);
    setCharge(merged.charge);
    setChargeMaxDist(merged.chargeMaxDist);
    setLinkDistance(merged.linkDistance);
    setLinkStrength(merged.linkStrength);
    setCollide(merged.collide);
    setCollideRadius(merged.collideRadius);
    setAlphaDecay(merged.alphaDecay);
    setGravity(merged.gravity);
    setLabelSize(merged.labelSize);
    setLabelSpacingScale(merged.labelSpacingScale);
    setLabelPadding(merged.labelPadding);
    setLabelWidthCap(merged.labelWidthCap);
    setLabelShowBackground(merged.labelShowBackground);
    setLabelBackgroundPadding(merged.labelBackgroundPadding);
    setLabelBackgroundOpacity(merged.labelBackgroundOpacity);
    setLabelBackgroundColor(merged.labelBackgroundColor);
    setLabelBackgroundRadius(merged.labelBackgroundRadius);
    setColaAvoidOverlaps(merged.colaAvoidOverlaps);
    setColaIterations(merged.colaIterations);
    setColaOverlapPadding(merged.colaOverlapPadding);
    setColaLinkLengthStrategy(merged.colaLinkLengthStrategy);
    setColaLinkLengthWeight(merged.colaLinkLengthWeight);
    setColaFlowAxis(merged.colaFlowAxis);
    setColaFlowSeparation(merged.colaFlowSeparation);
    setColaHandleDisconnected(merged.colaHandleDisconnected);
    setColaPinAfterDrag(merged.colaPinAfterDrag);
    setColaAutoStopMs(merged.colaAutoStopMs);
    setColaConvergenceThreshold(merged.colaConvergenceThreshold);
    setColaDragAlpha(merged.colaDragAlpha);
    setColaSettleAlpha(merged.colaSettleAlpha);
  };

  useEffect(() => {
    settingsLoadedRef.current = false;
    settingsLoadingRef.current = true;
    const unsub = auth.onAuthStateChanged(async (user) => {
      setSettingsUid(user?.uid ?? null);
      if (!user || !networkSettingsKey) {
        settingsLoadingRef.current = false;
        settingsLoadedRef.current = true;
        return;
      }
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        const data = snap.exists() ? (snap.data() as any) : null;
        const stored = data?.networkUiPrefs?.[networkSettingsKey] as Partial<NetworkUiSettings> | undefined;
        if (stored) {
          applySettings(stored);
        }
      } catch (err) {
        console.warn("Failed to load network UI settings", err);
      } finally {
        settingsLoadingRef.current = false;
        settingsLoadedRef.current = true;
      }
    });
    return () => unsub();
  }, [networkSettingsKey]);

  useEffect(() => {
    if (!settingsUid || !networkSettingsKey) return;
    if (!settingsLoadedRef.current || settingsLoadingRef.current) return;

    const payload: NetworkUiSettings = {
      layoutEngine,
      mode,
      selectedDim,
      selectedRespondent,
      sizeMode,
      colorScheme,
      edgeWidthMode,
      showArrows,
      hoverHighlightEnabled,
      nodeHoverTooltipEnabled,
      hideIsolates,
      showSelfLoops,
      highlightBidirectional,
      dupOffset,
      charge,
      chargeMaxDist,
      linkDistance,
      linkStrength,
      collide,
      collideRadius,
      alphaDecay,
      gravity,
      labelSize,
      labelSpacingScale,
      labelPadding,
      labelWidthCap,
      labelShowBackground,
      labelBackgroundPadding,
      labelBackgroundOpacity,
      labelBackgroundColor,
      labelBackgroundRadius,
      colaAvoidOverlaps,
      colaIterations,
      colaOverlapPadding,
      colaLinkLengthStrategy,
      colaLinkLengthWeight,
      colaFlowAxis,
      colaFlowSeparation,
      colaHandleDisconnected,
      colaPinAfterDrag,
      colaAutoStopMs,
      colaConvergenceThreshold,
      colaDragAlpha,
      colaSettleAlpha,
    };

    if (settingsSaveTimerRef.current) {
      window.clearTimeout(settingsSaveTimerRef.current);
    }
    settingsSaveTimerRef.current = window.setTimeout(async () => {
      const docRef = doc(db, "users", settingsUid);
      const writePayload = {
        [`networkUiPrefs.${networkSettingsKey}`]: {
          ...payload,
          updatedAt: Date.now(),
        },
      };
      try {
        await updateDoc(docRef, writePayload);
      } catch (err) {
        try {
          const snap = await getDoc(docRef);
          if (!snap.exists()) {
            await setDoc(docRef, { networkUiPrefs: { [networkSettingsKey]: { ...payload, updatedAt: Date.now() } } }, { merge: true });
          } else {
            console.warn("Failed to update network UI settings", err);
          }
        } catch (innerErr) {
          console.warn("Failed to save network UI settings", innerErr);
        }
      }
    }, 600);

    return () => {
      if (settingsSaveTimerRef.current) {
        window.clearTimeout(settingsSaveTimerRef.current);
      }
    };
  }, [
    settingsUid,
    networkSettingsKey,
    layoutEngine,
    mode,
    selectedDim,
    selectedRespondent,
    sizeMode,
    colorScheme,
    edgeWidthMode,
    showArrows,
    hoverHighlightEnabled,
    nodeHoverTooltipEnabled,
    hideIsolates,
    showSelfLoops,
    highlightBidirectional,
    dupOffset,
    charge,
    chargeMaxDist,
    linkDistance,
    linkStrength,
    collide,
    collideRadius,
    alphaDecay,
    gravity,
    labelSize,
    labelSpacingScale,
    labelPadding,
    labelWidthCap,
    labelShowBackground,
    labelBackgroundPadding,
    labelBackgroundOpacity,
    labelBackgroundColor,
    labelBackgroundRadius,
    colaAvoidOverlaps,
    colaIterations,
    colaOverlapPadding,
    colaLinkLengthStrategy,
    colaLinkLengthWeight,
    colaFlowAxis,
    colaFlowSeparation,
    colaHandleDisconnected,
    colaPinAfterDrag,
    colaAutoStopMs,
    colaConvergenceThreshold,
    colaDragAlpha,
    colaSettleAlpha,
  ]);

  useEffect(() => {
    if (COLA_DEBUG) console.log("[cola] debug logging enabled");
  }, []);

  // When drilling into a single respondent, hide isolates to reduce clutter
  useEffect(() => {
    if (selectedRespondent === "ALL") {
      setHideIsolates(false);
    } else {
      setHideIsolates(true);
    }
  }, [selectedRespondent]);

  // Track drawer height so the legend can avoid overlapping it
  useEffect(() => {
    const el = controlsRef.current;
    if (!el) {
      setControlsHeight(0);
      return;
    }

    const measure = () => {
      const rect = el.getBoundingClientRect();
      setControlsHeight(isDrawerOpen ? rect.height : 0);
    };

    if (typeof ResizeObserver === "undefined") {
      measure();
      return;
    }

    const observer = new ResizeObserver(() => {
      measure();
    });
    observer.observe(el);
    measure();

    return () => observer.disconnect();
  }, [isDrawerOpen]);


  // Apply respondent filter before downstream aggregations
  const filteredRawNomEdges = useMemo(() => {
    if (selectedRespondent === "ALL") return rawNomEdges;
    return rawNomEdges.filter((n) => n.respIndex === selectedRespondent);
  }, [rawNomEdges, selectedRespondent]);

  // Expand a nomination into the values for the requested dimension
  function projectNominationValues(n: RawNomEdge, dim: string): string[] {
    if (dim === DIM_COMBINED) {
      const all = new Set<string>();
      Object.values(n.fieldVals || {}).forEach((arr) => arr.forEach((v) => all.add(v)));
      return Array.from(all);
    }
    return n.fieldVals?.[dim] || [];
  }

  const orderedChoiceValues = useMemo(() => {
    if (selectedDim === DIM_COMBINED) {
      const combined: string[] = [];
      Object.values(howChoiceOrder || {}).forEach((arr) => {
        arr.forEach((v) => {
          if (!combined.includes(v)) combined.push(v);
        });
      });
      return combined;
    }
    return howChoiceOrder?.[selectedDim] ?? [];
  }, [howChoiceOrder, selectedDim]);

  // Current domain of how-values for the active dimension (post-filter)
  const howDomain = useMemo(() => {
    const vals = new Set<string>();
    filteredRawNomEdges.forEach((n) => projectNominationValues(n, selectedDim).forEach((v) => vals.add(v)));
    const dataOrder = Array.from(vals);
    const ordered: string[] = [];
    const seen = new Set<string>();
    orderedChoiceValues.forEach((v) => {
      if (vals.has(v) && !seen.has(v)) {
        ordered.push(v);
        seen.add(v);
      }
    });
    dataOrder.forEach((v) => {
      if (!seen.has(v)) {
        ordered.push(v);
        seen.add(v);
      }
    });
    return ordered;
  }, [filteredRawNomEdges, selectedDim, orderedChoiceValues]);

  const hasUnlabeled = useMemo(
    () => filteredRawNomEdges.some((n) => projectNominationValues(n, selectedDim).length === 0),
    [filteredRawNomEdges, selectedDim]
  );

  // Keep legend selection in sync with whatever values are currently available
  useEffect(() => {
    const all = new Set<string>(howDomain);
    if (hasUnlabeled) all.add(HOW_UNLABELED);
    setActiveHow(all);
  }, [howDomain, hasUnlabeled]);

  // Build links for the current dimension/mode/filters
  const links = useMemo(() => {
    type Row = { a: string; b: string; hows: string[] };
    const rows: Row[] = filteredRawNomEdges.map((n) => {
      const list = projectNominationValues(n, selectedDim);
      return { a: n.a, b: n.b, hows: list.length ? list : [HOW_UNLABELED] };
    });

    const filteredRows = rows
      .map((r) => ({ ...r, hows: r.hows.filter((h) => activeHow.has(h)) }))
      .filter((r) => r.hows.length > 0);

    if (mode === "rawNomination") {
      let perNom: LinkDatum[] = filteredRows.map((r) => ({
        source: r.a, target: r.b, sid: r.a, tid: r.b, w: 1, hows: r.hows,
      }));
      if (!showSelfLoops) perNom = perNom.filter((d) => d.sid !== d.tid);
      const groupsAB = d3.group(perNom, (d) => `${d.sid}→${d.tid}`);
      groupsAB.forEach((g) => g.forEach((d, i) => ((d._gIndex = i), (d._gCount = g.length))));
      markReverseEdges(perNom);
      return perNom;
    }

    if (mode === "aggregate") {
      const counts = d3.rollups(filteredRows, (arr) => arr.length, (r) => `${r.a}→${r.b}`);
      let agg: LinkDatum[] = counts.map(([key, w]) => {
        const [s, t] = key.split("→");
        return { source: s, target: t, sid: s, tid: t, w };
      });
      if (!showSelfLoops) agg = agg.filter((d) => d.sid !== d.tid);
      const groupsAB = d3.group(agg, (d) => `${d.sid}→${d.tid}`);
      groupsAB.forEach((g) => g.forEach((d, i) => ((d._gIndex = i), (d._gCount = g.length))));
      markReverseEdges(agg);
      return agg;
    }

    // Per-type mode: explode each visible how into its own edge
    const exploded = filteredRows.flatMap((r) => r.hows.map((h) => ({ a: r.a, b: r.b, how: h })));
    const counts = d3.rollups(exploded, (arr) => arr.length, (e) => `${e.a}→${e.b}→${e.how}`);
    let perHow: LinkDatum[] = counts.map(([k, w]) => {
      const [s, t, how] = k.split("→");
      return { source: s, target: t, sid: s, tid: t, w, how };
    });
    if (!showSelfLoops) perHow = perHow.filter((d) => d.sid !== d.tid);
    const groupsAB = d3.group(perHow, (d) => `${d.sid}→${d.tid}`);
    groupsAB.forEach((g) => g.forEach((d, i) => ((d._gIndex = i), (d._gCount = g.length))));
    markReverseEdges(perHow);
    return perHow;
  }, [filteredRawNomEdges, selectedDim, activeHow, mode, showSelfLoops]);

  // Optionally remove isolates from the rendered node list
  const nodesForView = useMemo(() => {
    if (!hideIsolates) return nodes;
    if (!links.length) return [];
    const touched = new Set<string>();
    links.forEach((l) => { touched.add(l.sid!); touched.add(l.tid!); });
    return nodes.filter((n) => touched.has(n.id));
  }, [nodes, links, hideIsolates]);

  // Keep pinned map in sync with the active node set
  useEffect(() => {
    if (!Object.keys(colaPinned).length) return;
    setColaPinned((prev) => {
      const ids = new Set(nodesForView.map((n) => n.id));
      let changed = false;
      const next: typeof prev = {};
      Object.entries(prev).forEach(([id, pos]) => {
        if (ids.has(id)) next[id] = pos;
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [nodesForView, colaPinned]);

  // Edge width scale obeys mode + explicit weight toggle
  const wScale = useMemo(() => {
    if (mode === "rawNomination" || edgeWidthMode === "fixed") return () => 1.5;
    let [min, max] = d3.extent(links, (d) => d.w ?? 1) as [number, number];
    if (min === undefined || max === undefined) return () => 1.5;
    if (min === max) { min = min - 0.001; max = max + 0.001; }
    const s = d3.scaleLinear().domain([min, max]).range([1, 5]);
    return (d: LinkDatum) => s(d.w ?? 1);
  }, [links, edgeWidthMode, mode]);

  // Node/edge styling scales
  const color = useMemo(() => {
    if (colorScheme === "blue") return () => "#3478bd";
    if (colorScheme === "turbo") {
      const degs = nodes.map((d) => d.degree ?? 0);
      const min = Math.min(...degs, 0);
      const max = Math.max(...degs, 1);
      const s = d3.scaleSequential(d3.interpolateTurbo).domain([min, max]);
      return (d: NodeDatum) => s(d.degree ?? 0);
    }
    const cat = d3.scaleOrdinal<string, string>(d3.schemeCategory10);
    return (d: NodeDatum) => cat(d.id);
  }, [colorScheme, nodes]);

  const rScale = useMemo(() => {
    if (sizeMode === "fixed") return () => 7;
    const extent = d3.extent(nodes, (d) => d.degree ?? 0) as [number, number];
    const s = d3.scaleSqrt().domain(extent).range([6, 16]);
    return (d: NodeDatum) => s(d.degree ?? 0);
  }, [nodes, sizeMode]);

  const howScale = useMemo(() => buildEdgeColorScale(howDomain), [howDomain]);

  const labelMetrics = useMemo(() => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const metrics = new Map<string, { text: string; width: number; height: number }>();
    if (!ctx) return metrics;
    ctx.font = `${labelSize}px ${LABEL_FONT_FAMILY}`;
    const ellipsis = "…";
    const ellipsisWidth = ctx.measureText(ellipsis).width;
    const cap = Math.max(20, labelWidthCap);
    nodesForView.forEach((n) => {
      const raw = n.id ?? "";
      let text = raw;
      let width = ctx.measureText(text).width;
      if (cap > 0 && width > cap) {
        let lo = 0;
        let hi = text.length;
        while (lo < hi) {
          const mid = Math.floor((lo + hi) / 2);
          const candidate = `${text.slice(0, mid)}${ellipsis}`;
          const w = ctx.measureText(candidate).width;
          if (w <= cap) lo = mid + 1;
          else hi = mid;
        }
        const keep = Math.max(0, lo - 1);
        text = `${text.slice(0, keep)}${ellipsis}`;
        width = Math.min(cap, Math.max(ellipsisWidth, ctx.measureText(text).width));
      }
      const height = labelSize * 1.2;
      metrics.set(n.id, { text, width, height });
    });
    return metrics;
  }, [nodesForView, labelSize, labelWidthCap]);

  const getLabelBox = (d: NodeDatum) => {
    const meta = labelMetrics.get(d.id);
    const textWidth = meta?.width ?? 0;
    const textHeight = meta?.height ?? labelSize * 1.2;
    const bgPad = labelBackgroundPadding;
    const rectWidth = textWidth + bgPad * 2;
    const rectHeight = textHeight + bgPad * 2;
    const offset = (rScale(d) + LABEL_BASE_GAP) * labelSpacingScale + labelPadding;
    const leftExtent = rScale(d);
    const rightExtent = offset + rectWidth;
    const width = Math.max(leftExtent + rightExtent, rScale(d) * 2);
    const height = Math.max(rScale(d) * 2, rectHeight);
    return { meta, textWidth, textHeight, rectWidth, rectHeight, offset, width, height };
  };

  useEffect(() => {
    d3GeomRef.current = { rScale, dupOffset, getLabelBox };
    colaGeomRef.current = { rScale, dupOffset, getLabelBox };
  }, [rScale, dupOffset, getLabelBox]);

  const edgeColor = useMemo(() => {
    if (mode === "aggregate" || mode === "rawNomination") {
      if (highlightBidirectional) {
        const accent = "#d32f2f";
        const base = "#bfc5ca";
        return (d: LinkDatum) => (d._hasReverse ? accent : base);
      }
      return () => "#bfc5ca";
    }
    if (!howDomain.length) return () => "#bfc5ca";
    return (d: LinkDatum) => (d.how ? (howScale as any)(d.how) : "#bfc5ca");
  }, [mode, howDomain, howScale, highlightBidirectional]);

  const nodeMetricsById = useMemo(() => {
    return computeNodeMetrics(nodesForView.map((n) => n.id), links);
  }, [nodesForView, links]);

  useEffect(() => {
    if (!nodeHoverTooltipEnabled) {
      setNodeHoverTooltip(null);
      return;
    }
    if (nodeHoverTooltip && !nodeMetricsById.has(nodeHoverTooltip.id)) {
      setNodeHoverTooltip(null);
    }
  }, [nodeHoverTooltipEnabled, nodeHoverTooltip, nodeMetricsById]);

  const setNodeTooltipFromEvent = (event: any, d: { id: string }) => {
    if (!nodeHoverTooltipEnabled) return;
    const evt = event as MouseEvent;
    const x = typeof evt.clientX === "number" ? evt.clientX : 0;
    const y = typeof evt.clientY === "number" ? evt.clientY : 0;
    setNodeHoverTooltip({ id: d.id, x: x + 14, y: y + 14 });
  };

  const clearNodeTooltip = () => setNodeHoverTooltip(null);

  // Preset helpers for quickly reconfiguring the simulation
  const applyPreset = (p: "compact" | "spread" | "noOverlap") => {
    if (layoutEngine === "cola") {
      if (p === "compact") {
        setLinkDistance(80);
        setColaAvoidOverlaps(false);
      }
      if (p === "spread") {
        setLinkDistance(180);
        setColaAvoidOverlaps(false);
      }
      if (p === "noOverlap") {
        setLinkDistance(130);
        setColaAvoidOverlaps(true);
        setColaOverlapPadding((prev) => Math.max(prev, 10));
      }
      return;
    }

    if (p === "compact") {
      setCharge(-120); setLinkDistance(80); setLinkStrength(0.8); setCollide(true); setCollideRadius(14); setAlphaDecay(0.05);
    }
    if (p === "spread") {
      setCharge(-600); setLinkDistance(180); setLinkStrength(0.5); setCollide(false); setAlphaDecay(0.02);
    }
    if (p === "noOverlap") {
      setCharge(-300); setLinkDistance(130); setLinkStrength(0.7); setCollide(true); setCollideRadius(20); setAlphaDecay(0.03);
    }
  };
  const gatherNow = () => {
    if (layoutEngine === "cola") {
      setLinkDistance(110);
      return;
    }
    setCharge(-220); setGravity(0.08); setLinkDistance(110); setAlphaDecay(0.02);
    const sim = simRef.current; if (sim) sim.alpha(0.8).restart();
  };

  // D3 render + simulation wiring
  useEffect(() => {
    if (layoutEngine !== "d3") return;
    const svgEl = svgRef.current;
    if (!svgEl) return;

    const svg = d3.select(svgEl);
    const prevXform = d3.zoomTransform(svg.node() as SVGSVGElement);
    svg.selectAll("*").remove();

    const g = svg.append("g");

    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .filter((event) => {
        // Allow zoom/pan unless interacting directly with a node (prevents dragging entire graph when moving a node)
        const target = event.target as Element | null;
        return !(target && target.tagName === "circle");
      })
      .scaleExtent([0.1, 6])
      .on("zoom", (e) => g.attr("transform", e.transform));
    zoomBehavior.current = zoom;
    svg.call(zoom as any);
    if (prevXform) svg.call(zoom.transform as any, prevXform);

    // Arrowhead marker definition
    const defs = svg.append("defs");
    defs.append("marker")
      .attr("id", "arrowhead")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", 20)
      .attr("refY", 0)
      .attr("markerWidth", 12)
      .attr("markerHeight", 12)
      .attr("markerUnits", "userSpaceOnUse")
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M0,-5L10,0L0,5")
      .attr("fill", "#555");

    const sim = d3.forceSimulation<NodeDatum, LinkDatum>(nodesForView)
      .alphaDecay(alphaDecay)
      .force("link", d3.forceLink<NodeDatum, LinkDatum>(links).id((d) => d.id).distance(linkDistance).strength(linkStrength))
      .force("charge", d3.forceManyBody().strength(charge).distanceMax(chargeMaxDist))
      .force("center", d3.forceCenter(wh.w / 2, wh.h / 2))
      .force("x", d3.forceX(wh.w / 2).strength(gravity))
      .force("y", d3.forceY(wh.h / 2).strength(gravity));

    if (collide) {
      sim.force(
        "collide",
        d3.forceCollide<NodeDatum>()
          .radius((d) => {
            const box = getLabelBox(d);
            const vertical = box.rectHeight / 2;
            const totalRight = box.offset + box.rectWidth;
            return Math.max(rScale(d) + collideRadius, totalRight + collideRadius, vertical + collideRadius);
          })
          .strength(0.9)
      );
    }
    else sim.force("collide", null);

    simRef.current = sim;

    const linkSel = g.append("g")
      .attr("fill", "none").attr("stroke-opacity", 0.9)
      .selectAll<SVGPathElement, LinkDatum>("path")
      .data(links, (d: any, i: number) => `${d.sid}→${d.tid}→${d.how ?? ""}#${d._gIndex ?? i}`)
      .join("path")
      .attr("stroke", (d) => edgeColor(d))
      .attr("stroke-width", (d) => wScale(d))
      .attr("marker-end", showArrows ? "url(#arrowhead)" : null);

    linkSel.append("title").text((d) => {
      if (mode === "aggregate") return `${d.sid} → ${d.tid}\nTotal (visible types): ${d.w ?? 1}`;
      if (mode === "rawNomination") {
        const list = (d.hows ?? []).join(", ") || "(Unlabeled)";
        return `${d.sid} → ${d.tid}\nNomination types: ${list}`;
      }
      const parts = [`${d.sid} → ${d.tid}`];
      if (d.how) parts.push(`Type: ${d.how}`);
      if (d.w) parts.push(`Weight: ${d.w}`);
      return parts.join("\n");
    });

    const nodeSel = g.append("g")
      .selectAll<SVGCircleElement, NodeDatum>("circle")
      .data(nodesForView, (d: any) => d.id)
      .join("circle")
      .attr("r", (d) => rScale(d))
      .attr("fill", (d) => color(d))
      .attr("stroke", "#fff")
      .attr("stroke-width", 1.5)
      .call(
        d3.drag<SVGCircleElement, NodeDatum>()
          .on("start", (e, d) => {
            (e.sourceEvent as any)?.stopPropagation();
            if (!e.active && !isFrozen) sim.alphaTarget(0.3).restart();
            d.fx = d.x; d.fy = d.y;
          })
          .on("drag", (e, d) => { d.fx = e.x; d.fy = e.y; if (isFrozen) updateNow(); })
          .on("end", (e, d) => { if (!e.active && !isFrozen) sim.alphaTarget(0); d.fx = null; d.fy = null; })
      );

    const labelSel = g.append("g")
      .style("pointer-events", "none")
      .selectAll<SVGGElement, NodeDatum>("g")
      .data(nodesForView, (d: any) => d.id)
      .join((enter) => {
        const gEnter = enter.append("g").attr("class", "node-label").style("pointer-events", "auto");
        gEnter.append("title");
        gEnter.append("rect");
        gEnter.append("text");
        return gEnter;
      });

    labelSel.style("pointer-events", "auto");

    labelSel.select("title").text((d) => d.id);

    labelSel.select("text")
      .text((d) => labelMetrics.get(d.id)?.text ?? d.id)
      .attr("font-size", labelSize)
      .attr("font-family", LABEL_FONT_FAMILY)
      .attr("dominant-baseline", "middle")
      .attr("x", labelBackgroundPadding)
      .attr("y", 0)
      .attr("paint-order", "stroke")
      .attr("stroke", "#fff")
      .attr("stroke-width", 3)
      .attr("stroke-opacity", 0.9)
      .attr("fill", "#111");

    labelSel.select("rect")
      .attr("display", labelShowBackground ? null : "none")
      .attr("fill", labelBackgroundColor)
      .attr("fill-opacity", labelBackgroundOpacity)
      .attr("rx", labelBackgroundRadius)
      .attr("ry", labelBackgroundRadius)
      .attr("x", 0)
      .attr("y", (d) => {
        const { rectHeight } = getLabelBox(d);
        return -rectHeight / 2;
      })
      .attr("width", (d) => getLabelBox(d).rectWidth)
      .attr("height", (d) => getLabelBox(d).rectHeight);

    const expandLabel = (event: any, d: NodeDatum) => {
      if (hoverHighlightEnabled) setActive(d.id);
      const label = d3.select(event.currentTarget as SVGGElement);
      label.raise();
      label.select("text").text(d.id);
      const textNode = label.select("text").node() as SVGTextElement | null;
      const fullWidth = textNode?.getComputedTextLength?.() ?? 0;
      label
        .select("rect")
        .attr("width", Math.max(getLabelBox(d).rectWidth, fullWidth + labelBackgroundPadding * 2));
    };

    const collapseLabel = (event: any, d: NodeDatum) => {
      setActive();
      const label = d3.select(event.currentTarget as SVGGElement);
      label.select("text").text(labelMetrics.get(d.id)?.text ?? d.id);
      label.select("rect").attr("width", getLabelBox(d).rectWidth);
    };

    const expandLabelForNode = (d: NodeDatum) => {
      if (hoverHighlightEnabled) setActive(d.id);
      const label = labelSel.filter((labelDatum: any) => labelDatum.id === d.id);
      label.raise();
      label.select("text").text(d.id);
      const textNode = label.select("text").node() as SVGTextElement | null;
      const fullWidth = textNode?.getComputedTextLength?.() ?? 0;
      label
        .select("rect")
        .attr("width", Math.max(getLabelBox(d).rectWidth, fullWidth + labelBackgroundPadding * 2));
    };

    const collapseLabelForNode = (d: NodeDatum) => {
      setActive();
      const label = labelSel.filter((labelDatum: any) => labelDatum.id === d.id);
      label.select("text").text(labelMetrics.get(d.id)?.text ?? d.id);
      label.select("rect").attr("width", getLabelBox(d).rectWidth);
    };

    labelSel
      .on("mouseover", expandLabel)
      .on("mouseout", collapseLabel);

    function setActive(nodeId?: string) {
      linkSel.attr("stroke-opacity", (d: any) => (!nodeId || d.sid === nodeId || d.tid === nodeId ? 0.95 : 0.15));
      nodeSel.attr("opacity", (d: any) => (!nodeId || d.id === nodeId ? 1 : 0.35));
      labelSel.attr("opacity", (d: any) => (!nodeId || d.id === nodeId ? 1 : 0.35));
    }
    if (hoverHighlightEnabled || nodeHoverTooltipEnabled) {
      nodeSel
        .on("mouseover", (event, d) => {
          expandLabelForNode(d);
          setNodeTooltipFromEvent(event, d);
        })
        .on("mousemove", (event, d) => setNodeTooltipFromEvent(event, d))
        .on("mouseout", (_event, d) => {
          collapseLabelForNode(d);
          clearNodeTooltip();
        });
    } else {
      nodeSel.on("mouseover", null).on("mousemove", null).on("mouseout", null);
      setActive();
      clearNodeTooltip();
    }

    // Geometry for self-loop edges
    function selfLoopPath(d: any) {
      const n = d.source as any;
      const x = (n.x as number) ?? 0;
      const y = (n.y as number) ?? 0;

      const rNode = d3GeomRef.current.rScale(n);
      const idx = d._gIndex ?? 0;
      const cnt = d._gCount ?? 1;

      const centerAngle = -Math.PI / 2;
      const open = 0.65; // radians
      const spread = Math.min(0.9, 0.28 * Math.max(0, cnt - 1));
      const offset = (idx - (cnt - 1) / 2) * (spread / Math.max(1, cnt - 1));

      const startAngle = centerAngle - open / 2 + offset;
      const endAngle = centerAngle + open / 2 + offset;

      const gap = Math.max(9, rNode * 0.55);
      const rs = rNode + gap, re = rNode + gap;

      const sx = x + rs * Math.cos(startAngle);
      const sy = y + rs * Math.sin(startAngle);
      const ex0 = x + re * Math.cos(endAngle);
      const ey0 = y + re * Math.sin(endAngle);

      const height = rNode * 2.4 + d3GeomRef.current.dupOffset * 0.7;
      let cx = x + (height + rNode) * Math.cos(centerAngle);
      let cy = y + (height + rNode) * Math.sin(centerAngle);
      cx += (idx - (cnt - 1) / 2) * (d3GeomRef.current.dupOffset * 0.6);

      const c1x = sx * 0.45 + cx * 0.55;
      const c1y = sy * 0.45 + cy * 0.55;
      const c2x = ex0 * 0.45 + cx * 0.55;
      const c2y = ey0 * 0.45 + cy * 0.55;

      const tx = ex0 - c2x;
      const ty = ey0 - c2y;
      const tlen = Math.hypot(tx, ty) || 1;
      const arrowPad = 25;
      const ex = ex0 + (tx / tlen) * arrowPad;
      const ey = ey0 + (ty / tlen) * arrowPad;

      return `M${sx},${sy} C${c1x},${c1y} ${c2x},${c2y} ${ex},${ey}`;
    }

    function curvedPath(d: any) {
      if (d.sid === d.tid) return selfLoopPath(d);
      const sx = (d.source as any).x as number, sy = (d.source as any).y as number;
      const tx = (d.target as any).x as number, ty = (d.target as any).y as number;

      const mx = (sx + tx) / 2, my = (sy + ty) / 2;

      const aFirst = String(d.sid) < String(d.tid);
      const ax = aFirst ? sx : tx, ay = aFirst ? sy : ty;
      const bx = aFirst ? tx : sx, by = aFirst ? ty : sy;
      const dxu = bx - ax, dyu = by - ay;
      const len = Math.hypot(dxu, dyu) || 1;
      const nxu = -dyu / len, nyu = dxu / len;

      const count = d._gCount ?? 1;
      const index = d._gIndex ?? 0;
      const sameDirOffset = (index - (count - 1) / 2) * d3GeomRef.current.dupOffset;

      const hasReverse = Boolean(d._hasReverse);
      const baseMag = Math.max(d3GeomRef.current.dupOffset * 0.8, Math.min(32, len * 0.2));
      const biDirBase = hasReverse ? baseMag : 0;

      const totalOffset = sameDirOffset + biDirBase;

      const cx = mx + nxu * totalOffset, cy = my + nyu * totalOffset;
      return `M${sx},${sy} Q${cx},${cy} ${tx},${ty}`;
    }

    const updateNow = () => {
      linkSel.attr("d", (d: any) => curvedPath(d));
      nodeSel.attr("cx", (d: any) => d.x!).attr("cy", (d: any) => d.y!);
      labelSel.attr("transform", (d: any) => {
        const { offset } = d3GeomRef.current.getLabelBox(d);
        return `translate(${d.x! + offset},${d.y!})`;
      });
    };
    (window as any).__updateNetworkNow = updateNow;

    d3SelectionsRef.current = { linkSel, nodeSel, labelSel, updateNow };

    sim.on("tick", updateNow);

    if (isFrozen) {
      const iters = Math.max(30, Math.min(600, 3 * nodesForView.length + links.length));
      sim.alpha(1); for (let i = 0; i < iters; i++) sim.tick(); updateNow(); sim.stop();
    }

    return () => {
      sim.stop();
      svg.selectAll("*").remove();
      d3SelectionsRef.current = null;
    };
  }, [
    layoutEngine,
    nodesForView,
    links,
    wh,
  ]);

  useEffect(() => {
    if (layoutEngine !== "cola") return;
    const state = colaSelectionsRef.current;
    const adapter = colaRef.current;
    if (!state || !adapter) return;

    const { linkSel, nodeSel, labelSel, colaNodes, updateCola } = state;

    linkSel
      .attr("stroke", (d) => edgeColor(d))
      .attr("stroke-width", (d) => wScale(d))
      .attr("marker-end", showArrows ? "url(#arrowhead)" : null);

    nodeSel
      .attr("r", (d: any) => rScale(d))
      .attr("fill", (d: any) => color(d));

    labelSel.select("text")
      .text((d: any) => labelMetrics.get(d.id)?.text ?? d.id)
      .attr("font-size", labelSize)
      .attr("font-family", LABEL_FONT_FAMILY)
      .attr("dominant-baseline", "middle")
      .attr("x", labelBackgroundPadding)
      .attr("y", 0)
      .attr("paint-order", "stroke")
      .attr("stroke", "#fff")
      .attr("stroke-width", 3)
      .attr("stroke-opacity", 0.9)
      .attr("fill", "#111");

    labelSel.select("title").text((d: any) => d.id);

    labelSel.select("rect")
      .attr("display", labelShowBackground ? null : "none")
      .attr("fill", labelBackgroundColor)
      .attr("fill-opacity", labelBackgroundOpacity)
      .attr("rx", labelBackgroundRadius)
      .attr("ry", labelBackgroundRadius)
      .attr("x", 0)
      .attr("y", (d: any) => {
        const { rectHeight } = getLabelBox(d);
        return -rectHeight / 2;
      })
      .attr("width", (d: any) => getLabelBox(d).rectWidth)
      .attr("height", (d: any) => getLabelBox(d).rectHeight);

    const setActive = (nodeId?: string) => {
      linkSel.attr("stroke-opacity", (d: any) => (!nodeId || d.sid === nodeId || d.tid === nodeId ? 0.95 : 0.15));
      nodeSel.attr("opacity", (d: any) => (!nodeId || d.id === nodeId ? 1 : 0.35));
      labelSel.attr("opacity", (d: any) => (!nodeId || d.id === nodeId ? 1 : 0.35));
    };
    if (hoverHighlightEnabled || nodeHoverTooltipEnabled) {
      nodeSel
        .on("mouseover", (event: any, d: any) => {
          if (hoverHighlightEnabled) setActive(d.id);
          setNodeTooltipFromEvent(event, d);
        })
        .on("mousemove", (event: any, d: any) => setNodeTooltipFromEvent(event, d))
        .on("mouseout", () => {
          setActive();
          clearNodeTooltip();
        });
    } else {
      nodeSel.on("mouseover", null).on("mousemove", null).on("mouseout", null);
      setActive();
      clearNodeTooltip();
    }

    colaNodes.forEach((node: any) => {
      const size = rScale(node) * 2 + 8 + colaOverlapPadding;
      const labelBox = getLabelBox(node);
      node.width = Math.max(size, labelBox.width + colaOverlapPadding);
      node.height = Math.max(size, labelBox.height + colaOverlapPadding);
      const pinned = colaPinned[node.id];
      if (pinned) {
        node.fixed = true;
        node.x = pinned.x;
        node.y = pinned.y;
        node.px = pinned.x;
        node.py = pinned.y;
      } else if (!colaPinAfterDrag) {
        node.fixed = false;
      }
    });

    adapter.avoidOverlaps(colaAvoidOverlaps);
    adapter.handleDisconnected(colaHandleDisconnected);
    adapter.size([wh.w, wh.h]);
    if (colaLinkLengthStrategy === "jaccard") {
      adapter.jaccardLinkLengths(colaLinkLengthWeight);
    } else {
      adapter.linkDistance(linkDistance);
    }
    if (colaFlowAxis === "x" || colaFlowAxis === "x-stagger") {
      adapter.flowLayout("x", Number(colaFlowSeparation));
    } else if (colaFlowAxis === "y" || colaFlowAxis === "y-stagger") {
      adapter.flowLayout("y", Number(colaFlowSeparation));
    }
    adapter.convergenceThreshold(colaConvergenceThreshold);

    updateCola();
    adapter.alpha(Math.max(0.02, colaDragAlpha));
    adapter.start(colaIterations, 0, colaIterations);
    if (colaStopTimerRef.current) window.clearTimeout(colaStopTimerRef.current);
    colaStopTimerRef.current = window.setTimeout(() => adapter.stop(), colaAutoStopMs);
  }, [
    layoutEngine,
    showArrows,
    rScale,
    color,
    labelSize,
    labelSpacingScale,
    labelPadding,
    labelWidthCap,
    labelMetrics,
    labelShowBackground,
    labelBackgroundPadding,
    labelBackgroundOpacity,
    labelBackgroundColor,
    labelBackgroundRadius,
    wScale,
    dupOffset,
    edgeColor,
    hoverHighlightEnabled,
    nodeHoverTooltipEnabled,
    colaAvoidOverlaps,
    colaOverlapPadding,
    colaLinkLengthStrategy,
    colaLinkLengthWeight,
    colaFlowAxis,
    colaFlowSeparation,
    colaHandleDisconnected,
    colaPinned,
    colaPinAfterDrag,
    colaConvergenceThreshold,
    colaDragAlpha,
    linkDistance,
    colaIterations,
    colaAutoStopMs,
    wh,
  ]);

  useEffect(() => {
    if (layoutEngine !== "d3") return;
    const state = d3SelectionsRef.current;
    const sim = simRef.current;
    if (!state || !sim) return;

    const { linkSel, nodeSel, labelSel, updateNow } = state;

    linkSel
      .attr("stroke", (d) => edgeColor(d))
      .attr("stroke-width", (d) => wScale(d))
      .attr("marker-end", showArrows ? "url(#arrowhead)" : null);

    nodeSel
      .attr("r", (d) => rScale(d))
      .attr("fill", (d) => color(d));

    labelSel.select("text")
      .text((d) => labelMetrics.get(d.id)?.text ?? d.id)
      .attr("font-size", labelSize)
      .attr("font-family", LABEL_FONT_FAMILY)
      .attr("dominant-baseline", "middle")
      .attr("x", labelBackgroundPadding)
      .attr("y", 0)
      .attr("paint-order", "stroke")
      .attr("stroke", "#fff")
      .attr("stroke-width", 3)
      .attr("stroke-opacity", 0.9)
      .attr("fill", "#111");

    labelSel.select("title").text((d) => d.id);

    labelSel.select("rect")
      .attr("display", labelShowBackground ? null : "none")
      .attr("fill", labelBackgroundColor)
      .attr("fill-opacity", labelBackgroundOpacity)
      .attr("rx", labelBackgroundRadius)
      .attr("ry", labelBackgroundRadius)
      .attr("x", 0)
      .attr("y", (d) => {
        const { rectHeight } = getLabelBox(d);
        return -rectHeight / 2;
      })
      .attr("width", (d) => getLabelBox(d).rectWidth)
      .attr("height", (d) => getLabelBox(d).rectHeight);

    const setActive = (nodeId?: string) => {
      linkSel.attr("stroke-opacity", (d: any) => (!nodeId || d.sid === nodeId || d.tid === nodeId ? 0.95 : 0.15));
      nodeSel.attr("opacity", (d: any) => (!nodeId || d.id === nodeId ? 1 : 0.35));
      labelSel.attr("opacity", (d: any) => (!nodeId || d.id === nodeId ? 1 : 0.35));
    };
    if (hoverHighlightEnabled || nodeHoverTooltipEnabled) {
      nodeSel
        .on("mouseover", (event, d) => {
          if (hoverHighlightEnabled) setActive(d.id);
          setNodeTooltipFromEvent(event, d);
        })
        .on("mousemove", (event, d) => setNodeTooltipFromEvent(event, d))
        .on("mouseout", () => {
          setActive();
          clearNodeTooltip();
        });
    } else {
      nodeSel.on("mouseover", null).on("mousemove", null).on("mouseout", null);
      setActive();
      clearNodeTooltip();
    }

    (sim.force("link") as d3.ForceLink<NodeDatum, LinkDatum>)
      .links(links)
      .distance(linkDistance)
      .strength(linkStrength);
    (sim.force("charge") as d3.ForceManyBody<NodeDatum>)
      .strength(charge)
      .distanceMax(chargeMaxDist);
    sim.force("center", d3.forceCenter(wh.w / 2, wh.h / 2));
    sim.force("x", d3.forceX(wh.w / 2).strength(gravity));
    sim.force("y", d3.forceY(wh.h / 2).strength(gravity));
    if (collide) {
      sim.force(
        "collide",
        d3.forceCollide<NodeDatum>()
          .radius((d) => {
            const box = getLabelBox(d);
            const vertical = box.rectHeight / 2;
            const totalRight = box.offset + box.rectWidth;
            return Math.max(rScale(d) + collideRadius, totalRight + collideRadius, vertical + collideRadius);
          })
          .strength(0.9)
      );
    } else {
      sim.force("collide", null);
    }
    sim.alphaDecay(alphaDecay);
    updateNow();
    if (!isFrozen) sim.alpha(0.35).restart();
  }, [
    layoutEngine,
    showArrows,
    charge,
    chargeMaxDist,
    gravity,
    linkDistance,
    linkStrength,
    collide,
    collideRadius,
    alphaDecay,
    rScale,
    color,
    labelSize,
    labelSpacingScale,
    labelPadding,
    labelWidthCap,
    labelMetrics,
    labelShowBackground,
    labelBackgroundPadding,
    labelBackgroundOpacity,
    labelBackgroundColor,
    labelBackgroundRadius,
    wScale,
    dupOffset,
    edgeColor,
    hoverHighlightEnabled,
    nodeHoverTooltipEnabled,
    mode,
    isFrozen,
    links,
    wh,
  ]);

  useEffect(() => {
    if (layoutEngine !== "cola") return;
    const svgEl = svgRef.current;
    if (!svgEl) return;
    let stopTimer: number | undefined;

    const svg = d3.select(svgEl);
    const prevXform = d3.zoomTransform(svg.node() as SVGSVGElement);
    svg.selectAll("*").remove();

    const g = svg.append("g");

    const zoom = d3.zoom<SVGSVGElement, unknown>().scaleExtent([0.1, 6]).on("zoom", (e) => g.attr("transform", e.transform));
    zoomBehavior.current = zoom;
    svg.call(zoom as any);
    if (prevXform) svg.call(zoom.transform as any, prevXform);

    const defs = svg.append("defs");
    defs.append("marker")
      .attr("id", "arrowhead")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", 20)
      .attr("refY", 0)
      .attr("markerWidth", 12)
      .attr("markerHeight", 12)
      .attr("markerUnits", "userSpaceOnUse")
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M0,-5L10,0L0,5")
      .attr("fill", "#555");

    const indexMap = new Map<string, number>();
    type ColaNode = NodeDatum & { sourceIndex?: number; targetIndex?: number; width?: number; height?: number; fixed?: boolean; px?: number; py?: number };

    // Precompute simple levels for stagger/radial layouts (BFS from highest-degree node)
    const levelMap = new Map<string, number>();
    if (colaFlowAxis !== "none") {
      const root = nodesForView.reduce((best, n) => {
        if (!best) return n;
        const deg = n.degree ?? 0;
        const bestDeg = best.degree ?? 0;
        return deg > bestDeg ? n : best;
      }, nodesForView[0] as NodeDatum | undefined);
      if (root) {
        const adj = new Map<string, Set<string>>();
        links.forEach((l) => {
          if (!l.sid || !l.tid) return;
          if (!adj.has(l.sid)) adj.set(l.sid, new Set());
          if (!adj.has(l.tid)) adj.set(l.tid, new Set());
          adj.get(l.sid)!.add(l.tid);
          adj.get(l.tid)!.add(l.sid);
        });
        const q: string[] = [root.id];
        levelMap.set(root.id, 0);
        while (q.length) {
          const cur = q.shift()!;
          const lvl = levelMap.get(cur) ?? 0;
          const neighbors = adj.get(cur);
          if (!neighbors) continue;
          neighbors.forEach((nbr) => {
            if (!levelMap.has(nbr)) {
              levelMap.set(nbr, lvl + 1);
              q.push(nbr);
            }
          });
        }
      }
    }
    const levelCounts = new Map<number, number>();
    levelMap.forEach((lvl) => levelCounts.set(lvl, (levelCounts.get(lvl) ?? 0) + 1));
    const levelIters = new Map<number, number>();

    const colaNodes: ColaNode[] = nodesForView.map((node, idx) => {
      indexMap.set(node.id, idx);
      const size = rScale(node) * 2 + 8 + colaOverlapPadding;
      const labelBox = getLabelBox(node);
      const width = Math.max(size, labelBox.width + colaOverlapPadding);
      const height = Math.max(size, labelBox.height + colaOverlapPadding);
      const pinned = colaPinned[node.id];
      let x = pinned?.x ?? node.x ?? Math.random() * wh.w;
      let y = pinned?.y ?? node.y ?? Math.random() * wh.h;
      const lvl = levelMap.get(node.id) ?? 0;
      if (colaFlowAxis === "x-stagger") {
        const offset = (lvl % 2 === 0 ? -1 : 1) * 30;
        y += offset;
      } else if (colaFlowAxis === "y-stagger") {
        // Place levels on a downward-facing semicircle to fan out labels
        const count = levelCounts.get(lvl) ?? 1;
        const idxInLevel = levelIters.get(lvl) ?? 0;
        levelIters.set(lvl, idxInLevel + 1);
        const cx = wh.w / 2;
        const cy = wh.h / 3;
        const radius = 180 + lvl * 140;
        const angleSpan = Math.PI; // 180 degrees
        const angle = count === 1
          ? Math.PI / 2
          : Math.PI + (idxInLevel / Math.max(1, count - 1)) * angleSpan;
        x = cx + radius * Math.cos(angle);
        y = cy + radius * Math.sin(angle);
      } else if (colaFlowAxis === "radial") {
        const count = levelCounts.get(lvl) ?? 1;
        const idxInLevel = levelIters.get(lvl) ?? 0;
        levelIters.set(lvl, idxInLevel + 1);
        const angle = (idxInLevel / Math.max(1, count)) * (Math.PI * 2);
        const radius = 140 + lvl * 140;
        const cx = wh.w / 2;
        const cy = wh.h / 2;
        x = cx + radius * Math.cos(angle);
        y = cy + radius * Math.sin(angle);
      }
      return {
        ...node,
        width,
        height,
        x,
        y,
        px: x,
        py: y,
        fixed: Boolean(pinned),
      };
    });

    const colaLinks = links.map((link) => {
      const sIdx = indexMap.get(link.sid!) ?? 0;
      const tIdx = indexMap.get(link.tid!) ?? 0;
      return {
        ...link,
        source: sIdx,
        target: tIdx,
        sourceIndex: sIdx,
        targetIndex: tIdx,
      };
    });

    const linkSel = g.append("g")
      .attr("fill", "none").attr("stroke-opacity", 0.9)
      .selectAll<SVGPathElement, LinkDatum>("path")
      .data(colaLinks as any, (d: any, i: number) => `${d.sid}→${d.tid}→${d.how ?? ""}#${d._gIndex ?? i}`)
      .join("path")
      .attr("stroke", (d) => edgeColor(d))
      .attr("stroke-width", (d) => wScale(d))
      .attr("marker-end", showArrows ? "url(#arrowhead)" : null);

    linkSel.append("title").text((d) => {
      if (mode === "aggregate") return `${d.sid} → ${d.tid}\nTotal (visible types): ${d.w ?? 1}`;
      if (mode === "rawNomination") {
        const list = (d.hows ?? []).join(", ") || "(Unlabeled)";
        return `${d.sid} → ${d.tid}\nNomination types: ${list}`;
      }
      const parts = [`${d.sid} → ${d.tid}`];
      if (d.how) parts.push(`Type: ${d.how}`);
      if (d.w) parts.push(`Weight: ${d.w}`);
      return parts.join("\n");
    });

    const nodeSel = g.append("g")
      .selectAll<SVGCircleElement, ColaNode>("circle")
      .data(colaNodes, (d: any) => d.id)
      .join("circle")
      .attr("r", (d) => rScale(d))
      .attr("fill", (d) => color(d))
      .attr("stroke", "#fff")
      .attr("stroke-width", 1.5);

    const labelSel = g.append("g")
      .style("pointer-events", "none")
      .selectAll<SVGGElement, ColaNode>("g")
      .data(colaNodes, (d: any) => d.id)
      .join((enter) => {
        const gEnter = enter.append("g").attr("class", "node-label").style("pointer-events", "auto");
        gEnter.append("title");
        gEnter.append("rect");
        gEnter.append("text");
        return gEnter;
      });

    labelSel.style("pointer-events", "auto");

    labelSel.select("title").text((d) => d.id);

    labelSel.select("text")
      .text((d) => labelMetrics.get(d.id)?.text ?? d.id)
      .attr("font-size", labelSize)
      .attr("font-family", LABEL_FONT_FAMILY)
      .attr("dominant-baseline", "middle")
      .attr("x", labelBackgroundPadding)
      .attr("y", 0)
      .attr("paint-order", "stroke")
      .attr("stroke", "#fff")
      .attr("stroke-width", 3)
      .attr("stroke-opacity", 0.9)
      .attr("fill", "#111");

    labelSel.select("rect")
      .attr("display", labelShowBackground ? null : "none")
      .attr("fill", labelBackgroundColor)
      .attr("fill-opacity", labelBackgroundOpacity)
      .attr("rx", labelBackgroundRadius)
      .attr("ry", labelBackgroundRadius)
      .attr("x", 0)
      .attr("y", (d) => {
        const { rectHeight } = getLabelBox(d);
        return -rectHeight / 2;
      })
      .attr("width", (d) => getLabelBox(d).rectWidth)
      .attr("height", (d) => getLabelBox(d).rectHeight);

    const expandLabel = (event: any, d: ColaNode) => {
      if (hoverHighlightEnabled) setActive(d.id);
      const label = d3.select(event.currentTarget as SVGGElement);
      label.raise();
      label.select("text").text(d.id);
      const textNode = label.select("text").node() as SVGTextElement | null;
      const fullWidth = textNode?.getComputedTextLength?.() ?? 0;
      label
        .select("rect")
        .attr("width", Math.max(getLabelBox(d).rectWidth, fullWidth + labelBackgroundPadding * 2));
    };

    const collapseLabel = (event: any, d: ColaNode) => {
      setActive();
      const label = d3.select(event.currentTarget as SVGGElement);
      label.select("text").text(labelMetrics.get(d.id)?.text ?? d.id);
      label.select("rect").attr("width", getLabelBox(d).rectWidth);
    };

    const expandLabelForNode = (d: ColaNode) => {
      if (hoverHighlightEnabled) setActive(d.id);
      const label = labelSel.filter((labelDatum: any) => labelDatum.id === d.id);
      label.raise();
      label.select("text").text(d.id);
      const textNode = label.select("text").node() as SVGTextElement | null;
      const fullWidth = textNode?.getComputedTextLength?.() ?? 0;
      label
        .select("rect")
        .attr("width", Math.max(getLabelBox(d).rectWidth, fullWidth + labelBackgroundPadding * 2));
    };

    const collapseLabelForNode = (d: ColaNode) => {
      setActive();
      const label = labelSel.filter((labelDatum: any) => labelDatum.id === d.id);
      label.select("text").text(labelMetrics.get(d.id)?.text ?? d.id);
      label.select("rect").attr("width", getLabelBox(d).rectWidth);
    };

    labelSel
      .on("mouseover", expandLabel)
      .on("mouseout", collapseLabel);

    function setActive(nodeId?: string) {
      linkSel.attr("stroke-opacity", (d: any) => (!nodeId || d.sid === nodeId || d.tid === nodeId ? 0.95 : 0.15));
      nodeSel.attr("opacity", (d: any) => (!nodeId || d.id === nodeId ? 1 : 0.35));
      labelSel.attr("opacity", (d: any) => (!nodeId || d.id === nodeId ? 1 : 0.35));
    }
    if (hoverHighlightEnabled || nodeHoverTooltipEnabled) {
      nodeSel
        .on("mouseover", (event, d) => {
          expandLabelForNode(d);
          setNodeTooltipFromEvent(event, d);
        })
        .on("mousemove", (event, d) => setNodeTooltipFromEvent(event, d))
        .on("mouseout", (_event, d) => {
          collapseLabelForNode(d);
          clearNodeTooltip();
        });
    } else {
      nodeSel.on("mouseover", null).on("mousemove", null).on("mouseout", null);
      setActive();
      clearNodeTooltip();
    }

    // Geometry for self-loop edges
    function selfLoopPath(d: any) {
      const n = d.source as any;
      const x = (n.x as number) ?? 0;
      const y = (n.y as number) ?? 0;

      const rNode = colaGeomRef.current.rScale(n);
      const idx = d._gIndex ?? 0;
      const cnt = d._gCount ?? 1;

      const centerAngle = -Math.PI / 2;
      const open = 0.65; // radians
      const spread = Math.min(0.9, 0.28 * Math.max(0, cnt - 1));
      const offset = (idx - (cnt - 1) / 2) * (spread / Math.max(1, cnt - 1));

      const startAngle = centerAngle - open / 2 + offset;
      const endAngle = centerAngle + open / 2 + offset;

      const gap = Math.max(9, rNode * 0.55);
      const rs = rNode + gap, re = rNode + gap;

      const sx = x + rs * Math.cos(startAngle);
      const sy = y + rs * Math.sin(startAngle);
      const ex0 = x + re * Math.cos(endAngle);
      const ey0 = y + re * Math.sin(endAngle);

      const height = rNode * 2.4 + colaGeomRef.current.dupOffset * 0.7;
      let cx = x + (height + rNode) * Math.cos(centerAngle);
      let cy = y + (height + rNode) * Math.sin(centerAngle);
      cx += (idx - (cnt - 1) / 2) * (colaGeomRef.current.dupOffset * 0.6);

      const c1x = sx * 0.45 + cx * 0.55;
      const c1y = sy * 0.45 + cy * 0.55;
      const c2x = ex0 * 0.45 + cx * 0.55;
      const c2y = ey0 * 0.45 + cy * 0.55;

      const tx = ex0 - c2x;
      const ty = ey0 - c2y;
      const tlen = Math.hypot(tx, ty) || 1;
      const arrowPad = 25;
      const ex = ex0 + (tx / tlen) * arrowPad;
      const ey = ey0 + (ty / tlen) * arrowPad;

      return `M${sx},${sy} C${c1x},${c1y} ${c2x},${c2y} ${ex},${ey}`;
    }

    function curvedPath(d: any) {
      if (d.sid === d.tid) return selfLoopPath(d);
      const sx = (d.source as any).x as number, sy = (d.source as any).y as number;
      const tx = (d.target as any).x as number, ty = (d.target as any).y as number;

      const mx = (sx + tx) / 2, my = (sy + ty) / 2;

      const aFirst = String(d.sid) < String(d.tid);
      const ax = aFirst ? sx : tx, ay = aFirst ? sy : ty;
      const bx = aFirst ? tx : sx, by = aFirst ? ty : sy;
      const dxu = bx - ax, dyu = by - ay;
      const len = Math.hypot(dxu, dyu) || 1;
      const nxu = -dyu / len, nyu = dxu / len;

      const count = d._gCount ?? 1;
      const index = d._gIndex ?? 0;
      const sameDirOffset = (index - (count - 1) / 2) * dupOffset;

      const hasReverse = Boolean(d._hasReverse);
      const baseMag = Math.max(dupOffset * 0.8, Math.min(32, len * 0.2));
      const biDirBase = hasReverse ? baseMag : 0;

      const totalOffset = sameDirOffset + biDirBase;

      const cx = mx + nxu * totalOffset, cy = my + nyu * totalOffset;
      return `M${sx},${sy} Q${cx},${cy} ${tx},${ty}`;
    }

    function curvedPathCola(d: any) {
      const srcNode = colaNodes[d.sourceIndex as number];
      const tgtNode = colaNodes[d.targetIndex as number];
      if (!srcNode || !tgtNode) return "";
      if (d.sid === d.tid) return selfLoopPath(d);
      const sx = srcNode.x ?? 0;
      const sy = srcNode.y ?? 0;
      const tx = tgtNode.x ?? 0;
      const ty = tgtNode.y ?? 0;
      const mx = (sx + tx) / 2;
      const my = (sy + ty) / 2;
      const aFirst = String(d.sid) < String(d.tid);
      const ax = aFirst ? sx : tx;
      const ay = aFirst ? sy : ty;
      const bx = aFirst ? tx : sx;
      const by = aFirst ? ty : sy;
      const dxu = bx - ax;
      const dyu = by - ay;
      const len = Math.hypot(dxu, dyu) || 1;
      const nxu = -dyu / len;
      const nyu = dxu / len;
      const count = d._gCount ?? 1;
      const index = d._gIndex ?? 0;
      const sameDirOffset = (index - (count - 1) / 2) * colaGeomRef.current.dupOffset;
      const hasReverse = Boolean(d._hasReverse);
      const baseMag = Math.max(colaGeomRef.current.dupOffset * 0.8, Math.min(32, len * 0.2));
      const biDirBase = hasReverse ? baseMag : 0;
      const totalOffset = sameDirOffset + biDirBase;
      const cx = mx + nxu * totalOffset;
      const cy = my + nyu * totalOffset;
      return `M${sx},${sy} Q${cx},${cy} ${tx},${ty}`;
    }

    function updateCola() {
      linkSel.attr("d", (d: any) => curvedPathCola(d));
      nodeSel.attr("cx", (d: any) => d.x!).attr("cy", (d: any) => d.y!);
      labelSel.attr("transform", (d: any) => {
        const { offset } = colaGeomRef.current.getLabelBox(d);
        return `translate(${d.x! + offset},${d.y!})`;
      });
    }

    const adapter = cola.d3adaptor(d3 as any)
      .avoidOverlaps(colaAvoidOverlaps)
      .handleDisconnected(colaHandleDisconnected)
      .size([wh.w, wh.h])
      .nodes(colaNodes as any)
      .links(colaLinks as any);
    colaRef.current = adapter;

    if (colaLinkLengthStrategy === "jaccard") {
      adapter.jaccardLinkLengths(colaLinkLengthWeight);
    } else {
      adapter.linkDistance(linkDistance);
    }

    if (colaFlowAxis === "x" || colaFlowAxis === "x-stagger") {
      adapter.flowLayout("x", Number(colaFlowSeparation));
    } else if (colaFlowAxis === "y" || colaFlowAxis === "y-stagger") {
      adapter.flowLayout("y", Number(colaFlowSeparation));
    }

    // Slightly higher threshold keeps layouts snappy and reduces slow drifting
    adapter.convergenceThreshold(colaConvergenceThreshold);
    adapter.on("tick", updateCola);
    adapter.start(colaIterations, 0, colaIterations);
    stopTimer = window.setTimeout(() => adapter.stop(), colaAutoStopMs);
    colaStopTimerRef.current = stopTimer;

    colaSelectionsRef.current = { linkSel, nodeSel, labelSel, colaNodes, colaLinks, updateCola };

    nodeSel.on(".drag", null); // clear any existing drag handlers (including cola defaults)
    let dragMoveCount = 0;
    let dragHasMoved = false;
    nodeSel.call(
      d3.drag<SVGCircleElement, ColaNode>()
        .on("start", (event, d: any) => {
          event.sourceEvent?.stopPropagation();
          if (stopTimer) window.clearTimeout(stopTimer);
          // Use drag event coords directly (already in local space) to avoid pointer/node offset
          const lx = event.x;
          const ly = event.y;
          d.x = d.x ?? lx ?? 0;
          d.y = d.y ?? ly ?? 0;
          d.px = d.px ?? d.x;
          d.py = d.py ?? d.y;
          d.fixed = true;
          d.fx = d.x;
          d.fy = d.y;
          dragMoveCount = 0;
          dragHasMoved = false;
          colaLog("drag start", { id: d.id, x: d.x, y: d.y, alpha: adapter.alpha() });
        })
        .on("drag", (event, d: any) => {
          const lx = event.x;
          const ly = event.y;
          d.x = lx;
          d.y = ly;
          d.px = lx;
          d.py = ly;
          d.fx = lx;
          d.fy = ly;
          d.fixed = true;
          updateCola();
          if (stopTimer) window.clearTimeout(stopTimer);
          if (!dragHasMoved) {
            adapter.alpha(colaDragAlpha);
            adapter.resume();
            dragHasMoved = true;
          } else {
            adapter.resume();
          }
          dragMoveCount += 1;
          if (COLA_DEBUG && dragMoveCount % 12 === 0) colaLog("drag tick", { id: d.id, x: d.x, y: d.y, alpha: adapter.alpha() });
        })
        .on("end", (event, d: any) => {
          const x = d.x ?? event.x ?? 0;
          const y = d.y ?? event.y ?? 0;
          if (colaPinAfterDrag) {
            (d as any).fixed = true;
            (d as any).px = x;
            (d as any).py = y;
            d.fx = x;
            d.fy = y;
            setColaPinned((prev) => ({ ...prev, [d.id]: { x, y } }));
            updateCola();
            adapter.stop();
            if (stopTimer) window.clearTimeout(stopTimer);
            if (COLA_DEBUG) colaLog("drag end (pinned, no settle)", { id: d.id, x, y });
            return;
          } else {
            d.fixed = false;
            d.fx = null;
            d.fy = null;
            delete (d as any).px;
            delete (d as any).py;
          }
          const releasePos = { x, y, alpha: adapter.alpha() };
          if (dragHasMoved) {
            adapter.alpha(Math.max(0.02, colaSettleAlpha * 0.7));
            adapter.resume();
            if (stopTimer) window.clearTimeout(stopTimer);
            const settleMs = Math.min(colaAutoStopMs, 500);
            stopTimer = window.setTimeout(() => adapter.stop(), settleMs);
          }
          if (COLA_DEBUG) {
            colaLog("drag end", { id: d.id, releasePos, pinned: colaPinAfterDrag });
            setTimeout(() => {
              const dx = (d.x ?? x) - x;
              const dy = (d.y ?? y) - y;
              const dist = Math.hypot(dx, dy);
              if (dist > 30) colaLog("post-settle jump", { id: d.id, releasePos, after: { x: d.x, y: d.y }, dist });
            }, 220);
          }
        })
    );
    simRef.current = null;

    return () => {
      if (stopTimer) window.clearTimeout(stopTimer);
      adapter.stop();
      svg.selectAll("*").remove();
      colaRef.current = null;
      colaSelectionsRef.current = null;
      colaStopTimerRef.current = null;
    };
  }, [
    layoutEngine,
    nodesForView,
    links,
    wh,
  ]);

const colaLog = (...args: any[]) => { if (COLA_DEBUG) console.log("[cola]", ...args); };

  // Pause/resume the simulation when the freeze toggle changes
  useEffect(() => {
    if (layoutEngine !== "d3") return;
    const sim = simRef.current;
    if (!sim) return;
    if (isFrozen) sim.stop();
    else sim.alpha(0.6).restart();
  }, [isFrozen, layoutEngine]);

  // HUD dataset (dimension + legend + respondent aware)
  const [hudOpen, setHudOpen] = useState<boolean>(false);
  const [hudRespectFilters, setHudRespectFilters] = useState<boolean>(true);
  const [hudQuery, setHudQuery] = useState<string>("");
  const [hudSort, setHudSort] = useState<{ key: "a" | "b" | "hows" | "respIndex"; dir: 1 | -1 }>({ key: "a", dir: 1 });

  const totalNomCount = useMemo(() => rawNomEdges.length, [rawNomEdges]);

  // Keyboard shortcuts: "H" toggles HUD, "D" opens the debug layout page
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const key = (e.key || "").toLowerCase();
      if (key !== "h" && key !== "d") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const focusEl = e.target as HTMLElement | null;
      if (focusEl) {
        const tag = focusEl.tagName;
        const isFormField = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || focusEl.isContentEditable;
        if (isFormField) return;
      }
      if (key === "h") {
        setHudOpen((prev) => !prev);
      } else if (key === "d" && surveyId && source && target) {
        window.location.assign(`/network/${surveyId}/${source}/${target}/debug`);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [surveyId, source, target]);

  const hudDataset = useMemo(() => {
    const rows = filteredRawNomEdges.map((n) => {
      const vals = projectNominationValues(n, selectedDim);
      const base = vals.length ? vals : [HOW_UNLABELED];
      if (!hudRespectFilters) return { ...n, visHows: base, hidHows: [] };
      const vis = base.filter((h) => activeHow.has(h));
      const hid = base.filter((h) => !activeHow.has(h));
      return { ...n, visHows: vis, hidHows: hid };
    });

    const rows2 = (hudRespectFilters && !showSelfLoops) ? rows.filter((n) => n.a !== n.b) : rows;
    const rows3 = hudRespectFilters ? rows2.filter((n) => n.visHows.length > 0) : rows2;

    const q = hudQuery.trim().toLowerCase();
    const filtered = q
      ? rows3.filter((n) => {
        const vis = n.visHows.join(", ");
        const hid = n.hidHows.join(", ");
        return (
          n.a.toLowerCase().includes(q) ||
          n.b.toLowerCase().includes(q) ||
          vis.toLowerCase().includes(q) ||
          hid.toLowerCase().includes(q)
        );
      })
      : rows3;

    const sorted = [...filtered].sort((x: any, y: any) => {
      const dir = hudSort.dir;
      let vx: string | number;
      let vy: string | number;
      if (hudSort.key === "hows") { vx = x.visHows.join(", "); vy = y.visHows.join(", "); }
      else { vx = x[hudSort.key]; vy = y[hudSort.key]; }
      if (vx < vy) return -1 * dir;
      if (vx > vy) return 1 * dir;
      return 0;
    });

    const showingNomCount = sorted.length;
    const showingEdgeCount = sorted.reduce((acc, n) => acc + n.visHows.length, 0);

    return { sorted, showingNomCount, showingEdgeCount };
  }, [filteredRawNomEdges, selectedDim, hudRespectFilters, activeHow, hudQuery, hudSort, showSelfLoops]);

  // Disable adjustable widths in per-nomination mode (weight is always 1)
  const edgeWidthDisabled = mode === "rawNomination";

  const nodeTooltipMetrics = useMemo(() => {
    if (!nodeHoverTooltip) return null;
    return nodeMetricsById.get(nodeHoverTooltip.id) ?? null;
  }, [nodeHoverTooltip, nodeMetricsById]);

  const formatMetric = (value: number, digits = 0) => {
    if (!Number.isFinite(value)) return "0";
    if (digits === 0) return String(Math.round(value));
    return value.toFixed(digits);
  };

  const renderLabelControls = () => (
    <ControlGroup title="LABELS" sx={{ minWidth: 520 }}>
        <ControlCard>
          <Stack direction="row" spacing={0.4} alignItems="center">
            <Typography variant="caption" sx={CONTROL_LABEL_SX}>Label size: {labelSize}px</Typography>
            <Tooltip title="Font size used for node labels." placement="top">
              <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
            </Tooltip>
          </Stack>
          <Slider size="small" min={8} max={28} step={1} value={labelSize} onChange={(_, v) => setLabelSize(v as number)} />
        </ControlCard>

        <ControlCard>
          <Stack direction="row" spacing={0.4} alignItems="center">
            <Typography variant="caption" sx={CONTROL_LABEL_SX}>Label spacing: {labelSpacingScale.toFixed(2)}x</Typography>
            <Tooltip title="Scales distance between node center and label." placement="top">
              <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
            </Tooltip>
          </Stack>
          <Slider size="small" min={0.6} max={2.5} step={0.05} value={labelSpacingScale} onChange={(_, v) => setLabelSpacingScale(v as number)} />
        </ControlCard>

        <ControlCard>
          <Stack direction="row" spacing={0.4} alignItems="center">
            <Typography variant="caption" sx={CONTROL_LABEL_SX}>Label padding: {labelPadding}px</Typography>
            <Tooltip title="Extra gap between node and label." placement="top">
              <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
            </Tooltip>
          </Stack>
          <Slider size="small" min={0} max={24} step={1} value={labelPadding} onChange={(_, v) => setLabelPadding(v as number)} />
        </ControlCard>

        <ControlCard>
          <Stack direction="row" spacing={0.4} alignItems="center">
            <Typography variant="caption" sx={CONTROL_LABEL_SX}>Label width cap: {labelWidthCap}px</Typography>
            <Tooltip title="Max label width before truncation." placement="top">
              <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
            </Tooltip>
          </Stack>
          <Slider size="small" min={60} max={600} step={10} value={labelWidthCap} onChange={(_, v) => setLabelWidthCap(v as number)} />
        </ControlCard>

        <ControlCard flex="1 1 220px" minWidth={200}>
          <Stack direction="row" spacing={0.4} alignItems="center">
            <FormControlLabel
              control={<Switch size="small" checked={labelShowBackground} onChange={(_, v) => setLabelShowBackground(v)} />}
              label={<Typography variant="caption" sx={CONTROL_LABEL_SX}>Label background</Typography>}
              sx={{ m: 0 }}
            />
            <Tooltip title="Toggle label background pill." placement="top">
              <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.6 }}>
            <Typography variant="caption" sx={CONTROL_LABEL_SX}>Color</Typography>
            <TextField
              type="color"
              size="small"
              value={labelBackgroundColor}
              onChange={(e) => setLabelBackgroundColor(e.target.value)}
              sx={{ width: 64 }}
              disabled={!labelShowBackground}
            />
          </Stack>
        </ControlCard>

        <ControlCard>
          <Stack direction="row" spacing={0.4} alignItems="center">
            <Typography variant="caption" sx={CONTROL_LABEL_SX}>Background padding: {labelBackgroundPadding}px</Typography>
            <Tooltip title="Extra space inside the label background." placement="top">
              <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
            </Tooltip>
          </Stack>
          <Slider
            size="small"
            min={0}
            max={12}
            step={1}
            value={labelBackgroundPadding}
            onChange={(_, v) => setLabelBackgroundPadding(v as number)}
            disabled={!labelShowBackground}
          />
        </ControlCard>

        <ControlCard>
          <Stack direction="row" spacing={0.4} alignItems="center">
            <Typography variant="caption" sx={CONTROL_LABEL_SX}>Background opacity: {labelBackgroundOpacity.toFixed(2)}</Typography>
            <Tooltip title="Opacity of the label background." placement="top">
              <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
            </Tooltip>
          </Stack>
          <Slider
            size="small"
            min={0.1}
            max={1}
            step={0.05}
            value={labelBackgroundOpacity}
            onChange={(_, v) => setLabelBackgroundOpacity(v as number)}
            disabled={!labelShowBackground}
          />
        </ControlCard>

        <ControlCard>
          <Stack direction="row" spacing={0.4} alignItems="center">
            <Typography variant="caption" sx={CONTROL_LABEL_SX}>Background radius: {labelBackgroundRadius}px</Typography>
            <Tooltip title="Corner radius for label background." placement="top">
              <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
            </Tooltip>
          </Stack>
          <Slider
            size="small"
            min={0}
            max={16}
            step={1}
            value={labelBackgroundRadius}
            onChange={(_, v) => setLabelBackgroundRadius(v as number)}
            disabled={!labelShowBackground}
          />
        </ControlCard>
    </ControlGroup>
  );

  const renderD3LayoutControls = () => (
    <ControlGroup title="FORCE D3 LAYOUT FORCES" sx={{ minWidth: 520 }}>
      <ControlCard>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
              <Typography variant="caption" sx={CONTROL_LABEL_SX}>Charge: {charge}</Typography>
              <Tooltip title="Repulsive force between nodes." placement="top">
                <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
              </Tooltip>
            </Box>
            <Slider size="small" min={-1200} max={-50} step={10} value={charge} onChange={(_, v) => setCharge(v as number)} />
          </ControlCard>

          <ControlCard>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
              <Typography variant="caption" sx={CONTROL_LABEL_SX}>Charge range: {chargeMaxDist}px</Typography>
              <Tooltip title="Limit how far repulsion reaches." placement="top">
                <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
              </Tooltip>
            </Box>
            <Slider size="small" min={120} max={600} step={10} value={chargeMaxDist} onChange={(_, v) => setChargeMaxDist(v as number)} />
          </ControlCard>

          <ControlCard>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
              <Typography variant="caption" sx={CONTROL_LABEL_SX}>Link distance: {linkDistance}px</Typography>
              <Tooltip title="Desired edge length." placement="top">
                <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
              </Tooltip>
            </Box>
            <Slider size="small" min={40} max={300} step={5} value={linkDistance} onChange={(_, v) => setLinkDistance(v as number)} />
          </ControlCard>

          <ControlCard>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
              <Typography variant="caption" sx={CONTROL_LABEL_SX}>Link strength: {linkStrength.toFixed(2)}</Typography>
              <Tooltip title="How strongly edges pull together." placement="top">
                <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
              </Tooltip>
            </Box>
            <Slider size="small" min={0.1} max={1} step={0.05} value={linkStrength} onChange={(_, v) => setLinkStrength(v as number)} />
          </ControlCard>

          <ControlCard flex="1 1 220px" minWidth={170}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <FormControlLabel
                control={<Checkbox size="small" checked={collide} onChange={(e) => setCollide(e.target.checked)} />}
                label={<Typography variant="caption" sx={CONTROL_LABEL_SX}>Collide</Typography>}
                sx={{ m: 0 }}
              />
              <Tooltip title="Nudge nodes apart to reduce overlap." placement="top">
                <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
              </Tooltip>
            </Box>
            <Slider
              size="small"
              min={8}
              max={40}
              step={1}
              value={collideRadius}
              onChange={(_, v) => setCollideRadius(v as number)}
              disabled={!collide}
            />
          </ControlCard>

          <ControlCard>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
              <Typography variant="caption" sx={CONTROL_LABEL_SX}>Alpha decay: {alphaDecay.toFixed(2)}</Typography>
              <Tooltip title="Higher stops the sim faster." placement="top">
                <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
              </Tooltip>
            </Box>
            <Slider size="small" min={0.005} max={0.08} step={0.005} value={alphaDecay} onChange={(_, v) => setAlphaDecay(v as number)} />
          </ControlCard>

          <ControlCard>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
              <Typography variant="caption" sx={CONTROL_LABEL_SX}>Gravity: {gravity.toFixed(2)}</Typography>
              <Tooltip title="Pull nodes gently toward the center." placement="top">
                <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
              </Tooltip>
            </Box>
            <Slider size="small" min={0} max={0.15} step={0.005} value={gravity} onChange={(_, v) => setGravity(v as number)} />
      </ControlCard>
    </ControlGroup>
  );

  const renderColaLayoutControls = () => (
    <ControlGroup title="WEBCOLA LAYOUT OPTIONS" sx={{ minWidth: 420 }}>
      <ControlCard>
          <Stack direction="row" spacing={0.4} alignItems="center">
            <Typography variant="caption" sx={CONTROL_LABEL_SX}>Link distance: {linkDistance}px</Typography>
            <Tooltip title="Desired edge length (WebCola)." placement="top">
              <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
            </Tooltip>
          </Stack>
          <Slider size="small" min={40} max={300} step={5} value={linkDistance} onChange={(_, v) => setLinkDistance(v as number)} />
      </ControlCard>

      <ControlCard>
          <Stack direction="row" spacing={0.4} alignItems="center">
            <Typography variant="caption" sx={CONTROL_LABEL_SX}>Link lengths</Typography>
            <Select
              size="small"
              value={colaLinkLengthStrategy}
              onChange={(e) => setColaLinkLengthStrategy(e.target.value as any)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="fixed">Fixed distance</MenuItem>
              <MenuItem value="jaccard">Jaccard (similarity-based)</MenuItem>
            </Select>
            <Tooltip title="Choose how edge lengths are computed. Jaccard uses shared neighbors to set lengths." placement="top">
              <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
            </Tooltip>
          </Stack>
          {colaLinkLengthStrategy === "jaccard" && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" sx={CONTROL_LABEL_SX}>Jaccard weight: {colaLinkLengthWeight}</Typography>
              <Slider
                size="small"
                min={0.1}
                max={5}
                step={0.1}
                value={colaLinkLengthWeight}
                onChange={(_, v) => setColaLinkLengthWeight(v as number)}
              />
            </Box>
          )}
      </ControlCard>
    </ControlGroup>
  );

  const renderDynamicsControls = () => {
    return (
      <ControlGroup title="WEBCOLA DYNAMICS" sx={{ minWidth: 520 }}>
        <ControlCard>
          <Stack direction="row" spacing={0.4} alignItems="center">
            <Typography variant="caption" sx={CONTROL_LABEL_SX}>Drag responsiveness: {colaDragAlpha.toFixed(2)}</Typography>
            <Tooltip title="Higher values make the layout respond more while dragging." placement="top">
              <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
            </Tooltip>
          </Stack>
          <Slider size="small" min={0.05} max={0.4} step={0.01} value={colaDragAlpha} onChange={(_, v) => setColaDragAlpha(v as number)} />
        </ControlCard>

        <ControlCard>
          <Stack direction="row" spacing={0.4} alignItems="center">
            <Typography variant="caption" sx={CONTROL_LABEL_SX}>Settle alpha: {colaSettleAlpha.toFixed(2)}</Typography>
            <Tooltip title="Energy kick after releasing a node; lower values settle more gently." placement="top">
              <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
            </Tooltip>
          </Stack>
          <Slider size="small" min={0.02} max={0.25} step={0.01} value={colaSettleAlpha} onChange={(_, v) => setColaSettleAlpha(v as number)} />
        </ControlCard>

        <ControlCard>
          <Stack direction="row" spacing={0.4} alignItems="center">
            <Typography variant="caption" sx={CONTROL_LABEL_SX}>Settle after: {(colaAutoStopMs / 1000).toFixed(1)}s</Typography>
            <Tooltip title="How long WebCola keeps solving before auto-stopping after updates or drags." placement="top">
              <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
            </Tooltip>
          </Stack>
          <Slider size="small" min={300} max={4000} step={100} value={colaAutoStopMs} onChange={(_, v) => setColaAutoStopMs(v as number)} />
        </ControlCard>

        <ControlCard>
          <Stack direction="row" spacing={0.4} alignItems="center">
            <Typography variant="caption" sx={CONTROL_LABEL_SX}>Iterations: {colaIterations}</Typography>
            <Tooltip title="Number of solver iterations; higher can improve layout at the cost of time." placement="top">
              <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
            </Tooltip>
          </Stack>
          <Slider size="small" min={10} max={200} step={5} value={colaIterations} onChange={(_, v) => setColaIterations(v as number)} />
        </ControlCard>

        <ControlCard>
          <Stack direction="row" spacing={0.4} alignItems="center">
            <Typography variant="caption" sx={CONTROL_LABEL_SX}>Convergence threshold: {colaConvergenceThreshold.toFixed(3)}</Typography>
            <Tooltip title="Lower values converge more tightly but can take longer." placement="top">
              <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
            </Tooltip>
          </Stack>
          <Slider size="small" min={0.002} max={0.05} step={0.001} value={colaConvergenceThreshold} onChange={(_, v) => setColaConvergenceThreshold(v as number)} />
        </ControlCard>

        <ControlCard>
          <Stack direction="row" spacing={0.4} alignItems="center">
            <FormControlLabel
              control={<Checkbox size="small" checked={colaAvoidOverlaps} onChange={(_, v) => setColaAvoidOverlaps(v)} />}
              label={<Typography variant="caption" sx={CONTROL_LABEL_SX}>Avoid overlaps</Typography>}
              sx={{ m: 0 }}
            />
            <Tooltip title="Prevents nodes from overlapping; may spread the layout." placement="top">
              <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
            </Tooltip>
          </Stack>
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" sx={CONTROL_LABEL_SX}>Overlap padding: {colaOverlapPadding}px</Typography>
            <Tooltip title="Extra padding applied to nodes when avoiding overlaps." placement="top">
              <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
            </Tooltip>
            <Slider
              size="small"
              min={0}
              max={40}
              step={2}
              value={colaOverlapPadding}
              onChange={(_, v) => setColaOverlapPadding(v as number)}
            />
          </Box>
        </ControlCard>

      </ControlGroup>
    );
  };

  const renderRibbonContent = () => {
    const disabledBox = (disabled: boolean, child: ReactNode) => (
      <Box sx={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? "none" : "auto" }}>
        {child}
      </Box>
    );

    if (ribbonTab === "view") {
      return (
        <>
          <ControlGroup title="VIEW" sx={{ minWidth: 360 }}>
            <ControlCard>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.35, flexWrap: "wrap" }}>
                {[{
                  label: "Arrows",
                  checked: showArrows,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => setShowArrows(e.target.checked),
                }, {
                  label: "Hover highlight",
                  checked: hoverHighlightEnabled,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => setHoverHighlightEnabled(e.target.checked),
                }, {
                  label: "Node hover tooltip",
                  checked: nodeHoverTooltipEnabled,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => setNodeHoverTooltipEnabled(e.target.checked),
                }, {
                  label: "Hide isolates",
                  checked: hideIsolates,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => setHideIsolates(e.target.checked),
                }, {
                  label: "Show self-nominations",
                  checked: showSelfLoops,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => setShowSelfLoops(e.target.checked),
                }].map((cfg) => (
                  <FormControlLabel
                    key={cfg.label}
                    sx={{ m: 0, px: 0.35, py: 0.15, borderRadius: 0.6, "&:hover": { bgcolor: "rgba(0,0,0,0.035)" } }}
                    control={<Checkbox size="small" checked={cfg.checked} onChange={cfg.onChange} />}
                    label={<Typography variant="caption" sx={{ ...CONTROL_LABEL_SX, fontSize: "0.68rem", lineHeight: 1.1 }}>{cfg.label}</Typography>}
                  />
                ))}
              </Box>
            </ControlCard>

            <ControlCard flex="1 1 220px" minWidth={180}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.35, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  variant={highlightBidirectional ? "contained" : "outlined"}
                  startIcon={<CompareArrowsIcon fontSize="small" />}
                  onClick={() => setHighlightBidirectional((v) => !v)}
                  disabled={mode === "perHow"}
                  sx={CONTROL_BUTTON_SX}
                >
                  Highlight bidirectional
                </Button>
              </Box>
            </ControlCard>

            <ControlCard flex="1 1 260px" minWidth={220}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                <Tooltip title="Zoom in" enterDelay={TOOLTIP_ENTER_DELAY}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (!svgRef.current || !zoomBehavior.current) return;
                      d3.select(svgRef.current).transition().duration(300).call(zoomBehavior.current.scaleBy as any, 1.2);
                    }}
                  >
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Zoom out" enterDelay={TOOLTIP_ENTER_DELAY}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (!svgRef.current || !zoomBehavior.current) return;
                      d3.select(svgRef.current).transition().duration(300).call(zoomBehavior.current.scaleBy as any, 0.8);
                    }}
                  >
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Fit to screen" enterDelay={TOOLTIP_ENTER_DELAY}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (!svgRef.current || !zoomBehavior.current) return;
                      d3.select(svgRef.current).transition().duration(300).call(zoomBehavior.current.transform as any, d3.zoomIdentity);
                    }}
                  >
                    <CenterFocusStrongIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </ControlCard>

            <ControlCard flex="1 1 240px" minWidth={200}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  variant={isFrozen ? "contained" : "outlined"}
                  sx={CONTROL_BUTTON_SX}
                  onClick={() => setIsFrozen((v) => !v)}
                  disabled={isCola}
                  color={isCola ? "inherit" : undefined}
                >
                  {isFrozen ? "Unfreeze" : "Freeze"}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  sx={CONTROL_BUTTON_SX}
                  startIcon={<DownloadIcon fontSize="small" />}
                  onClick={exportGraphImage}
                  disabled={exportingImage}
                >
                  {exportingImage ? "Exporting…" : "Export PNG"}
                </Button>
                <Button size="small" variant={hudOpen ? "contained" : "outlined"} onClick={() => setHudOpen((v) => !v)} sx={CONTROL_BUTTON_SX}>
                  HUD (H)
                </Button>
              </Box>
            </ControlCard>
          </ControlGroup>
        </>
      );
    }

    if (ribbonTab === "data") {
      return (
        <ControlGroup title="DATA & FILTERS" sx={{ minWidth: 520 }}>
          <ControlCard>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
              <Typography variant="caption" sx={CONTROL_LABEL_SX}>Mode</Typography>
              <Tooltip title="Switch between per-type, aggregate, or raw nomination modes. See Help for full details." placement="top" enterDelay={TOOLTIP_ENTER_DELAY}>
                <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
              </Tooltip>
            </Box>
            <Select size="small" value={mode} onChange={(e) => setMode(e.target.value as any)}>
              <MenuItem value="perHow">Per-Type</MenuItem>
              <MenuItem value="aggregate">Aggregate</MenuItem>
              <MenuItem value="rawNomination">Raw (per nomination)</MenuItem>
            </Select>
          </ControlCard>

          <ControlCard>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
              <Typography variant="caption" sx={CONTROL_LABEL_SX}>Respondent</Typography>
              <Tooltip title="Filter to a specific survey respondent." placement="top" enterDelay={TOOLTIP_ENTER_DELAY}>
                <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
              </Tooltip>
            </Box>
            <Select
              size="small"
              value={selectedRespondent as any}
              onChange={(e) => setSelectedRespondent(e.target.value as any)}
            >
              <MenuItem value="ALL">All respondents</MenuItem>
              {Array.from(new Set(filteredRawNomEdges.length ? rawNomEdges.map(e => e.respIndex) : rawNomEdges.map(e => e.respIndex)))
                .sort((a, b) => a - b)
                .map((idx) => (
                  <MenuItem key={idx} value={idx}>
                    {`Respondent ${idx + 1}`}
                  </MenuItem>
                ))}
            </Select>
          </ControlCard>

          <ControlCard>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
              <Typography variant="caption" sx={CONTROL_LABEL_SX}>Node size</Typography>
              <Tooltip title="Determines node size, fixed or degree-based." placement="top" enterDelay={TOOLTIP_ENTER_DELAY}>
                <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
              </Tooltip>
            </Box>
            <Select size="small" value={sizeMode} onChange={(e) => setSizeMode(e.target.value as any)}>
              <MenuItem value="fixed">Fixed</MenuItem>
              <MenuItem value="degree">By degree</MenuItem>
            </Select>
          </ControlCard>

          <ControlCard>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
              <Typography variant="caption" sx={CONTROL_LABEL_SX}>Color palette</Typography>
              <Tooltip title="Swap between categorical, monochrome, or turbo ramps." placement="top" enterDelay={TOOLTIP_ENTER_DELAY}>
                <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
              </Tooltip>
            </Box>
            <Select size="small" value={colorScheme} onChange={(e) => setColorScheme(e.target.value as any)}>
              <MenuItem value="category10">Categorical (10)</MenuItem>
              <MenuItem value="blue">Monochrome</MenuItem>
              <MenuItem value="turbo">Turbo ramp</MenuItem>
            </Select>
          </ControlCard>

          <ControlCard sx={{ opacity: edgeWidthDisabled ? 0.6 : 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
              <Typography variant="caption" sx={CONTROL_LABEL_SX}>Edge width</Typography>
              <Tooltip
                title={edgeWidthDisabled ? "Raw nominations: each edge weight = 1." : "Show fixed widths or scale by edge weight."}
                placement="top"
                enterDelay={TOOLTIP_ENTER_DELAY}
              >
                <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
              </Tooltip>
            </Box>
            <Select
              size="small"
              value={edgeWidthMode}
              onChange={(e) => setEdgeWidthMode(e.target.value as any)}
              disabled={edgeWidthDisabled}
            >
              <MenuItem value="fixed">Fixed</MenuItem>
              <MenuItem value="weight">By weight</MenuItem>
            </Select>
          </ControlCard>
        </ControlGroup>
      );
    }

    if (ribbonTab === "labels") {
      return renderLabelControls();
    }

    if (ribbonTab === "layout") {
      return (
        <>
          <ControlGroup title="LAYOUT ENGINE" sx={{ minWidth: 360 }}>
            <ControlCard flex="1 1 220px" minWidth={180}>
              <ToggleButtonGroup
                exclusive
                value={layoutEngine}
                size="small"
                onChange={(_, val) => val && setLayoutEngine(val as "d3" | "cola")}
                sx={{
                  borderRadius: 999,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "primary.main",
                  "& .MuiToggleButton-root": {
                    fontSize: "0.7rem",
                    px: 1.5,
                    textTransform: "uppercase",
                    fontWeight: 700,
                    border: 0,
                    "&.Mui-selected": {
                      color: "#fff",
                      bgcolor: "primary.main",
                      "&:hover": { bgcolor: "primary.dark" },
                    },
                    "&:not(.Mui-selected)": {
                      color: "primary.main",
                    },
                  },
                }}
              >
                <ToggleButton value="d3">Force D3</ToggleButton>
                <ToggleButton value="cola">WebCola</ToggleButton>
              </ToggleButtonGroup>
            </ControlCard>

            <ControlCard flex="1 1 240px" minWidth={200}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.35, flexWrap: "wrap" }}>
                <Button size="small" variant="outlined" sx={CONTROL_BUTTON_SX} onClick={() => applyPreset("compact")}>COMPACT</Button>
                <Button size="small" variant="outlined" sx={CONTROL_BUTTON_SX} onClick={() => applyPreset("spread")}>SPREAD</Button>
                <Button size="small" variant="outlined" sx={CONTROL_BUTTON_SX} onClick={() => applyPreset("noOverlap")}>NO OVERLAP</Button>
                <Button size="small" variant="contained" sx={CONTROL_BUTTON_SX} onClick={gatherNow}>GATHER</Button>
              </Box>
            </ControlCard>

            <ControlCard flex="1 1 220px" minWidth={200}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                <Typography variant="caption" sx={CONTROL_LABEL_SX}>Duplicate offset</Typography>
                <Tooltip title="Adjusts separation between parallel edges." placement="top" enterDelay={TOOLTIP_ENTER_DELAY}>
                  <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
                </Tooltip>
              </Box>
              <Slider
                size="small"
                min={0}
                max={30}
                step={1}
                value={dupOffset}
                onChange={(_, v) => setDupOffset(v as number)}
              />
            </ControlCard>
          </ControlGroup>

          {disabledBox(isCola, renderD3LayoutControls())}
          {disabledBox(!isCola, renderColaLayoutControls())}
        </>
      );
    }

    if (ribbonTab === "dynamics") {
      return disabledBox(!isCola, renderDynamicsControls());
    }

    if (ribbonTab === "advanced") {
      return (
        <ControlGroup title="ADVANCED" sx={{ minWidth: 360 }}>
          <ControlCard>
            <Stack direction="row" spacing={0.4} alignItems="center">
              <Typography variant="caption" sx={CONTROL_LABEL_SX}>Flow layout</Typography>
              <Select
                size="small"
                value={colaFlowAxis}
                onChange={(e) => setColaFlowAxis(e.target.value as any)}
                sx={{ minWidth: 140 }}
                disabled={!isCola}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="x">Left → Right</MenuItem>
                <MenuItem value="y">Top → Bottom</MenuItem>
                <MenuItem value="x-stagger">Left → Right (staggered)</MenuItem>
                <MenuItem value="y-stagger">Top → Bottom (staggered)</MenuItem>
                <MenuItem value="radial">Radial (hub-centered)</MenuItem>
              </Select>
              <Tooltip title="Optional flow alignment for directional graphs." placement="top">
                <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
              </Tooltip>
            </Stack>
            {colaFlowAxis !== "none" && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={CONTROL_LABEL_SX}>Flow separation: {colaFlowSeparation}px</Typography>
                <Slider
                  size="small"
                  min={20}
                  max={200}
                  step={5}
                  value={colaFlowSeparation}
                  onChange={(_, v) => setColaFlowSeparation(v as number)}
                  disabled={!isCola}
                />
              </Box>
            )}
          </ControlCard>

          <ControlCard>
            <Stack direction="row" spacing={0.4} alignItems="center">
              <FormControlLabel
                control={<Checkbox size="small" checked={colaHandleDisconnected} onChange={(_, v) => setColaHandleDisconnected(v)} />}
                label={<Typography variant="caption" sx={CONTROL_LABEL_SX}>Handle disconnected</Typography>}
                sx={{ m: 0 }}
                disabled={!isCola}
              />
              <Tooltip title="Pack disconnected components separately for better spacing." placement="top">
                <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
              </Tooltip>
            </Stack>
          </ControlCard>

          <ControlCard>
            <Stack direction="row" spacing={0.4} alignItems="center">
              <FormControlLabel
                control={<Checkbox size="small" checked={colaPinAfterDrag} onChange={(_, v) => setColaPinAfterDrag(v)} />}
                label={<Typography variant="caption" sx={CONTROL_LABEL_SX}>Pin after drag</Typography>}
                sx={{ m: 0 }}
                disabled={!isCola}
              />
              <Tooltip title="When enabled, dragged nodes stay fixed at their new positions." placement="top">
                <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
              </Tooltip>
              <Button size="small" sx={{ ml: 1 }} onClick={() => setColaPinned({})} disabled={!isCola}>
                Reset pinned
              </Button>
            </Stack>
          </ControlCard>
        </ControlGroup>
      );
    }

    return null;
  };

  // React render
  return (
    <Box sx={{ position: "relative", height: "100vh", bgcolor: "#fafafa" }}>
      {/* Top controls bar */}
      <Box
        ref={controlsRef}
        sx={{
          position: "absolute", top: 0, left: 0, right: 0, p: 0.5, bgcolor: "rgba(255,255,255,0.95)",
          display: "flex", flexWrap: "wrap", gap: 0.9, alignItems: "center", zIndex: 10,
          boxShadow: "0 2px 6px rgba(0,0,0,.08)",
          transition: "max-height 0.3s ease-in-out, padding 0.3s ease-in-out",
          maxHeight: isDrawerOpen ? "80vh" : "0",
          overflowY: isDrawerOpen ? "auto" : "hidden",
          padding: isDrawerOpen ? "6px" : "0",
        }}
      >
        {/* Ribbon header */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexWrap: "wrap",
            gap: 0.6,
            alignItems: "center",
            bgcolor: "rgba(33,150,243,0.04)",
            border: "1px solid rgba(15,76,129,0.08)",
            borderRadius: 1,
            p: 0.6,
          }}
        >
          <ToggleButtonGroup
            exclusive
            value={ribbonTab}
            size="small"
            onChange={(_, val) => val && setRibbonTab(val as any)}
            sx={{
              borderRadius: 999,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "primary.main",
              "& .MuiToggleButton-root": {
                fontSize: "0.7rem",
                px: 1.4,
                textTransform: "uppercase",
                fontWeight: 700,
                border: 0,
                "&.Mui-selected": {
                  color: "#fff",
                  bgcolor: "primary.main",
                  "&:hover": { bgcolor: "primary.dark" },
                },
                "&:not(.Mui-selected)": {
                  color: "primary.main",
                },
              },
            }}
          >
            <ToggleButton value="view">View</ToggleButton>
            <ToggleButton value="data">Data</ToggleButton>
            <ToggleButton value="labels">Labels</ToggleButton>
            <ToggleButton value="layout">Layout</ToggleButton>
            <ToggleButton value="dynamics">Dynamics</ToggleButton>
            <ToggleButton value="advanced">Advanced</ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ flex: 1 }} />

          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              if (surveyId && source && target) {
                window.location.assign(`/network/${surveyId}/${source}/${target}/details`);
              }
            }}
            sx={CONTROL_BUTTON_SX}
          >
            View Details
          </Button>
          <Button size="small" variant="outlined" startIcon={<HelpOutline />} onClick={() => setIsHelpModalOpen(true)} sx={CONTROL_BUTTON_SX}>
            Help
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={() => applySettings(DEFAULT_NETWORK_UI_SETTINGS)}
            sx={CONTROL_BUTTON_SX}
          >
            Reset
          </Button>
          <Button size="small" variant="outlined" startIcon={<VisibilityOff />} onClick={() => setIsDrawerOpen(false)} sx={CONTROL_BUTTON_SX}>
            Hide Controls
          </Button>
        </Box>

        {/* Ribbon content */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "flex-start",
            gap: 0.8,
            mt: 0.4,
            pt: 0.4,
            borderTop: "1px solid rgba(0,0,0,0.04)",
            overflowX: "auto",
            flexWrap: "nowrap",
          }}
        >
          {renderRibbonContent()}
        </Box>
    </Box>

  {!isDrawerOpen && (
    <Button
      variant="contained" size="small" onClick={() => setIsDrawerOpen(true)}
          sx={{ position: "absolute", top: 16, right: 16, zIndex: 11, display: "flex", alignItems: "center", gap: 1 }}
        >
          <Visibility fontSize="small" /> Show Controls
        </Button>
      )}

      {/* Legend */}
      <LegendWrapper
        legendDisabled={mode === "aggregate"}
        selectedDim={selectedDim}
        setSelectedDim={setSelectedDim}
        dimensions={dimensions}
        howDomain={howDomain}
        hasUnlabeled={hasUnlabeled}
        activeHow={activeHow}
        setActiveHow={setActiveHow}
        howScale={howScale}
        drawerHeight={controlsHeight}
        drawerOpen={isDrawerOpen}
      />

      {/* HUD */}
      {hudOpen && (
        <Paper elevation={3} sx={{
          position: "absolute", top: 0, right: 0, height: "100%",
          width: { xs: "95%", sm: 420, md: 500 }, zIndex: 20, display: "flex", flexDirection: "column",
          borderLeft: "1px solid #e0e0e0", bgcolor: "#fff",
        }}>
          <Box sx={{ p: 1.5, borderBottom: "1px solid #eee", display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Nominations HUD</Typography>
            <Box sx={{ flex: 1 }} />
            <FormControlLabel
              sx={{ mr: 1 }}
              control={<Switch size="small" checked={hudRespectFilters} onChange={(_, v) => setHudRespectFilters(v)} />}
              label={<Typography variant="caption">Respect legend filters</Typography>}
            />
            <IconButton size="small" onClick={() => setHudOpen(false)}><CloseIcon fontSize="small" /></IconButton>
          </Box>

          <Box sx={{ p: 1.5, borderBottom: "1px solid #eee", display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <TextField size="small" fullWidth placeholder="Search A, B or Types…" value={hudQuery} onChange={(e) => setHudQuery(e.target.value)} />
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>Sort:</Typography>
              <Select size="small" value={hudSort.key} onChange={(e) => setHudSort((s) => ({ ...s, key: e.target.value as any }))}>
                <MenuItem value="a">Source (A)</MenuItem>
                <MenuItem value="b">Target (B)</MenuItem>
                <MenuItem value="hows">Types</MenuItem>
                <MenuItem value="respIndex">Response #</MenuItem>
              </Select>
              <Select size="small" value={hudSort.dir} onChange={(e) => setHudSort((s) => ({ ...s, dir: Number(e.target.value) as 1 | -1 }))}>
                <MenuItem value={1}>Asc</MenuItem>
                <MenuItem value={-1}>Desc</MenuItem>
              </Select>
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflow: "auto" }}>
            <TableContainer component={Box} sx={{ maxHeight: "100%" }}>
              <Table stickyHeader size="small" aria-label="nominations">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12, whiteSpace: "nowrap" }}>Resp</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Source (A)</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Target (B)</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Type(s)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hudDataset.sorted.map((n: any, i: number) => (
                    <TableRow key={`${n.respIndex}-${n.a}→${n.b}-${i}`}>
                      <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>{i + 1}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>{`#${n.respIndex + 1}`}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{n.a}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{n.b}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {n.visHows.length > 0 && (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: n.hidHows.length ? 0.25 : 0 }}>
                            {n.visHows.map((h: string, j: number) => (
                              <Chip
                                key={`vis-${h}-${j}`}
                                size="small"
                                label={h}
                                sx={{ mb: 0.5, bgcolor: (howScale as any)(h), color: "#111", border: "1px solid rgba(0,0,0,0.06)" }}
                              />
                            ))}
                          </Stack>
                        )}
                        {n.hidHows.length > 0 && (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {n.hidHows.map((h: string, j: number) => (
                              <Chip
                                key={`hid-${h}-${j}`}
                                size="small"
                                variant="outlined"
                                label={h}
                                title="Hidden by legend filter"
                                sx={{ mb: 0.5, opacity: 0.35, borderColor: (howScale as any)(h), color: "#333" }}
                              />
                            ))}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>#{n.respIndex + 1}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box sx={{ p: 1, borderTop: '1px solid #eee', display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Nominations: <b>{totalNomCount}</b>
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Showing: <b>{hudDataset.showingNomCount}</b> (<b>{hudDataset.showingEdgeCount}</b> edges)
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Tip: Press <b>H</b> to toggle HUD
            </Typography>
          </Box>
        </Paper>
      )}

      {nodeHoverTooltipEnabled && nodeHoverTooltip && nodeTooltipMetrics && (
        <Paper
          elevation={6}
          sx={{
            position: "fixed",
            left: Math.max(8, Math.min(nodeHoverTooltip.x, wh.w - 280)),
            top: Math.max(8, Math.min(nodeHoverTooltip.y, wh.h - 220)),
            zIndex: 25,
            minWidth: 240,
            maxWidth: 280,
            p: 1,
            pointerEvents: "none",
            border: "1px solid rgba(0,0,0,0.08)",
            bgcolor: "rgba(255,255,255,0.97)",
            backdropFilter: "blur(2px)",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.6 }}>
            {nodeTooltipMetrics.id}
          </Typography>
          <Table size="small" sx={{ "& td": { borderBottom: "none", py: 0.35, px: 0.5, fontSize: 12 } }}>
            <TableBody>
              <TableRow><TableCell sx={{ color: "text.secondary" }}>In-degree</TableCell><TableCell align="right">{formatMetric(nodeTooltipMetrics.inDegree)}</TableCell></TableRow>
              <TableRow><TableCell sx={{ color: "text.secondary" }}>Out-degree</TableCell><TableCell align="right">{formatMetric(nodeTooltipMetrics.outDegree)}</TableCell></TableRow>
              <TableRow><TableCell sx={{ color: "text.secondary" }}>Total degree</TableCell><TableCell align="right">{formatMetric(nodeTooltipMetrics.degree)}</TableCell></TableRow>
              <TableRow><TableCell sx={{ color: "text.secondary" }}>Betweenness</TableCell><TableCell align="right">{formatMetric(nodeTooltipMetrics.betweenness, 2)}</TableCell></TableRow>
              <TableRow><TableCell sx={{ color: "text.secondary" }}>Closeness</TableCell><TableCell align="right">{formatMetric(nodeTooltipMetrics.closeness, 3)}</TableCell></TableRow>
            </TableBody>
          </Table>
        </Paper>
      )}

      <svg ref={svgRef} width={wh.w} height={wh.h} />
      <HelpModal open={isHelpModalOpen} handleClose={() => setIsHelpModalOpen(false)} />
    </Box>
  );
}

/* Legend component wrapper */
function LegendWrapper({
  legendDisabled,
  selectedDim,
  setSelectedDim,
  dimensions,
  howDomain,
  hasUnlabeled,
  activeHow,
  setActiveHow,
  howScale,
  drawerHeight,
  drawerOpen,
}: {
  legendDisabled: boolean;
  selectedDim: string;
  setSelectedDim: (v: string) => void;
  dimensions: { name: string; label: string }[];
  howDomain: string[];
  hasUnlabeled: boolean;
  activeHow: Set<string>;
  setActiveHow: React.Dispatch<React.SetStateAction<Set<string>>>;
  howScale: d3.ScaleOrdinal<string, string, never>;
  drawerHeight?: number;
  drawerOpen: boolean;
}) {
  const DEFAULT_TOP = 16;
  const [legendPos, setLegendPos] = useState<{ top: number; left: number }>({ top: DEFAULT_TOP, left: 16 });
  const [legendCollapsed, setLegendCollapsed] = useState<boolean>(true);
  const dragDataRef = useRef<{ dx: number; dy: number; dragging: boolean }>({ dx: 0, dy: 0, dragging: false });
  const [wh, setWh] = useState({ w: window.innerWidth, h: window.innerHeight });
  const minTop = drawerHeight && drawerHeight > 0 ? drawerHeight + 8 : DEFAULT_TOP;
  const drawerAutoStateRef = useRef<{ forced: boolean; userMovedAfter: boolean }>({ forced: false, userMovedAfter: false });
  useEffect(() => {
    const onResize = () => setWh({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    setLegendPos((pos) => {
      if (pos.top < minTop) {
        if (drawerHeight && drawerHeight > 0) {
          drawerAutoStateRef.current = { forced: true, userMovedAfter: false };
        }
        return { ...pos, top: minTop };
      }
      return pos;
    });
  }, [minTop, drawerHeight]);

  useEffect(() => {
    if (!drawerOpen) {
      if (drawerAutoStateRef.current.forced && !drawerAutoStateRef.current.userMovedAfter) {
        setLegendPos((pos) => ({ ...pos, top: DEFAULT_TOP }));
      }
      drawerAutoStateRef.current = { forced: false, userMovedAfter: false };
    }
  }, [drawerOpen]);

  const onLegendDown = (e: React.MouseEvent | React.TouchEvent) => {
    if (drawerAutoStateRef.current.forced) {
      drawerAutoStateRef.current.userMovedAfter = true;
    }
    const pt = "touches" in e ? e.touches[0] : (e as React.MouseEvent);
    dragDataRef.current.dragging = true;
    dragDataRef.current.dx = pt.clientX - legendPos.left;
    dragDataRef.current.dy = pt.clientY - legendPos.top;
    window.addEventListener("mousemove", onLegendMove as any);
    window.addEventListener("mouseup", onLegendUp as any);
    window.addEventListener("touchmove", onLegendMove as any, { passive: false });
    window.addEventListener("touchend", onLegendUp as any);
  };
  const onLegendMove = (e: MouseEvent | TouchEvent) => {
    if (!dragDataRef.current.dragging) return;
    const pt = e instanceof TouchEvent ? e.touches[0] : (e as MouseEvent);
    if (e instanceof TouchEvent) e.preventDefault();
    const left = Math.max(0, Math.min(pt.clientX - dragDataRef.current.dx, wh.w - 120));
    const top = Math.max(minTop, Math.min(pt.clientY - dragDataRef.current.dy, wh.h - 80));
    setLegendPos({ left, top });
  };
  const onLegendUp = () => {
    dragDataRef.current.dragging = false;
    window.removeEventListener("mousemove", onLegendMove as any);
    window.removeEventListener("mouseup", onLegendUp as any);
    window.removeEventListener("touchmove", onLegendMove as any);
    window.removeEventListener("touchend", onLegendUp as any);
  };

  const toggleHow = (key: string, opts?: { solo?: boolean }) => {
    if (legendDisabled) return;
    setActiveHow((prev: Set<string>) => {
      if (opts?.solo) return new Set([key]);
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const setAllHows = () => {
    if (legendDisabled) return;
    const all = new Set<string>(howDomain);
    if (hasUnlabeled) all.add(HOW_UNLABELED);
    setActiveHow(all);
  };
  const setNoHows = () => {
    if (legendDisabled) return;
    setActiveHow(new Set());
  };

  const chip = (label: string, color: string | undefined) => {
    const active = activeHow.has(label);
    return (
      <Chip
        key={label}
        size="small"
        label={label}
        onClick={legendDisabled ? undefined : (e) => toggleHow(label, { solo: (e as any).shiftKey || (e as any).metaKey })}
        sx={{
          mb: 1,
          borderColor: legendDisabled ? "#eee" : active ? "#666" : "#ddd",
          borderWidth: 1,
          borderStyle: "solid",
          opacity: legendDisabled ? 0.35 : active ? 1 : 0.3,
          color: "#111",
          backgroundColor: legendDisabled ? "#e6eaee" : color ?? "#bfc5ca",
          cursor: legendDisabled ? "not-allowed" : "pointer",
        }}
      />
    );
  };

  return (
    <Paper elevation={2} sx={{
      position: "absolute", top: legendPos.top, left: legendPos.left, zIndex: 12,
      p: legendCollapsed ? 0.75 : 1.5, pr: 1, bgcolor: "rgba(255,255,255,0.9)", maxWidth: 560,
      cursor: dragDataRef.current.dragging ? "grabbing" : "default", userSelect: "none",
    }}>
      {/* Legend header + drag handle */}
      <Box onMouseDown={onLegendDown} onTouchStart={onLegendDown}
        sx={{ mb: legendCollapsed ? 0 : 1, display: "flex", alignItems: "center", gap: 1, cursor: "grab" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {legendDisabled ? "Relationship type (legend disabled in Aggregate)" : "Relationship type (edge colors)"}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={() => setLegendCollapsed((v) => !v)} sx={{ ml: 0.5 }}
          title={legendCollapsed ? "Expand legend" : "Collapse legend"}>
          {legendCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Typography variant="caption" sx={{ color: "text.secondary", mr: 1 }}>Dimension</Typography>
      {/* Dimension toggle */}
      <Select
        size="small" value={selectedDim}
        onChange={(e) => setSelectedDim(e.target.value)}
        sx={{ minWidth: 160, bgcolor: "#fff" }}
      >
        {dimensions.map((d) => (
          <MenuItem key={d.name} value={d.name}>{d.label}</MenuItem>
        ))}
      </Select>

      {!legendCollapsed && (
        <>
          {!legendDisabled && (
            <Box sx={{ mb: 1, display: "flex", gap: 1 }}>
              <Button size="small" onClick={setAllHows}>ALL</Button>
              <Button size="small" onClick={setNoHows}>NONE</Button>
            </Box>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {howDomain.map((h) => chip(h, (howScale as any)(h)))}
            {hasUnlabeled && chip(HOW_UNLABELED, undefined)}
          </Stack>
        </>
      )}
    </Paper>
  );
}

/* Help modal */
function HelpModal({ open, handleClose }: { open: boolean; handleClose: () => void }) {
  const helpContent = [
    { control: "Dimension", function: "Switch between Combined and individual questions inside the target panel. Fully recomputes edges & legend." },
    {
      control: "Mode",
      function: (
        <>
          <strong>Per-type</strong>
          <br />
          One edge per A→B per relationship type (how). Line width reflects how many nominations selected that specific type.
          <br />
          <br />
          <strong>Aggregate</strong>
          <br />
          One edge per A→B that sums all visible types. Line width scales from 1 to 5 pixels based on total nominations between A and B across distinct survey responses. Multiple purposes within the same response do not increase aggregate weight.
          <br />
          <br />
          <strong>Raw (per nomination)</strong>
          <br />
          Every nomination is drawn as its own edge; all edges use a uniform width.
        </>
      ),
    },
    { control: "Legend", function: "Clickable in Per-Type and Raw modes; disabled in Aggregate." },
    { control: "Show self-nominations", function: "Toggle A→A edges on canvas and in HUD (when respecting filters)." },
    { control: "HUD (H)", function: "Right-side panel with all nominations, colored chips." },
  ];
  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={{
        p: 4, width: "90%", maxWidth: 800, margin: "auto", mt: "5vh", bgcolor: "white",
        border: "2px solid #000", borderRadius: 2, boxShadow: 24, outline: "none",
        maxHeight: "90vh", overflowY: "auto"
      }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Network Graph Controls Help</Typography>
        <Typography sx={{ mb: 3 }}>Hover any control for a quick tip.</Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Control</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Function</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {helpContent.map((row, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ fontWeight: "bold" }}>{row.control}</TableCell>
                  <TableCell>{row.function}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Button onClick={handleClose} sx={{ mt: 3, float: "right" }}>Close</Button>
      </Box>
    </Modal>
  );
}
